import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import Footer from "../../components/Footer";
import UpdatePayment from "../../components/UpdatePayment";
import CTAs from "../../components/CTAs";
import { getCurrentUser } from "../../utils/getCurrentUser";
import { stripeService } from "../../services/stripeService";
import { paystackService } from "../../services/paystackService";

import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Eye, Download, Send, X, Search, FileText,
  ChevronLeft, ChevronRight, Mail, Plus, Edit2,
  Check, AlertCircle, Clock, Settings, Upload, CloudUpload,
  RefreshCw, Link,
} from "lucide-react";

/* ─── helpers ─── */
const formatCurrency = (amount, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount || 0);

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

const getStatusStyle = (status) => {
  const map = {
    PAID:       { background: "#F0FDF4", color: "#166534" },
    AUTHORISED: { background: "#EFF6FF", color: "#1E40AF" },
    SUBMITTED:  { background: "#FFFBEB", color: "#92400E" },
    OVERDUE:    { background: "#FEF2F2", color: "#991B1B" },
    PART_PAID:  { background: "#FFF7ED", color: "#C2410C" },
    DRAFT:      { background: "#F1F5F9", color: "#475569" },
    VOIDED:     { background: "#F1F5F9", color: "#475569" },
    DELETED:    { background: "#F1F5F9", color: "#475569" },
    UPLOADED:   { background: "#F5F3FF", color: "#6D28D9" },
    QUICKBOOKS: { background: "#F0FDF4", color: "#15803D" },
  };
  return map[status] || { background: "#F1F5F9", color: "#475569" };
};

/* ─── date-range helpers (Xero invoice date filter) ─── */
// Furthest back a range can start. The server enforces the same limit.
const MAX_RANGE_YEARS = 3;

// Local-time "YYYY-MM-DD" (toISOString() would shift the day in BST).
const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getTodayValue = () => toDateInputValue(new Date());

const getMinRangeValue = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MAX_RANGE_YEARS);
  return toDateInputValue(d);
};

const RANGE_PRESETS = [
  { id: "30d", label: "Last 30 days",   getFrom: () => { const d = new Date(); d.setDate(d.getDate() - 30); return toDateInputValue(d); } },
  { id: "3m",  label: "Last 3 months",  getFrom: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return toDateInputValue(d); } },
  { id: "12m", label: "Last 12 months", getFrom: () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return toDateInputValue(d); } },
  { id: "3y",  label: "Last 3 years",   getFrom: () => getMinRangeValue() },
];

// Returns an error string, or "" when the range is usable.
const validateRange = (from, to) => {
  if (!from || !to) return "Choose both a from and a to date.";
  if (from > to) return "The from date must be on or before the to date.";
  if (from < getMinRangeValue()) return `The from date can't be more than ${MAX_RANGE_YEARS} years ago.`;
  if (to > getTodayValue()) return "The to date can't be in the future.";
  return "";
};

// True when a range object is a usable { from, to } pair. Also guards the
// sync handlers against being handed a click event instead of a range.
const isRange = (range) => !!(range && typeof range.from === "string" && typeof range.to === "string" && range.from && range.to);

// Body for POST /api/{provider}/sync — the range when there is one.
const syncBody = (range) => (isRange(range) ? JSON.stringify({ from: range.from, to: range.to }) : undefined);

// Invoice date as "YYYY-MM-DD" whatever the provider stored (Keap stores a
// full timestamp, the others a plain date).
const getInvoiceDateValue = (invoice) => {
  const raw = invoice?.invoiceDates;
  if (!raw) return "";
  const str = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? "" : toDateInputValue(parsed);
};

/* ─── helper: get invoice id regardless of source ─── */
const getInvoiceId = (invoice) => invoice.id || invoice._id;

/* ─── Xero logo SVG ─── */
const XeroLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#13B5EA"/>
    <path d="M11 11l10 10M21 11L11 21" stroke="white" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

/* ─── QuickBooks logo SVG ─── */
const QBLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#2CA01C"/>
    <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm0 3a2.5 2.5 0 0 1 2.5 2.5v1H20a1 1 0 1 1 0 2h-1.5v5H20a1 1 0 1 1 0 2h-1.5v1a2.5 2.5 0 0 1-5 0v-1H12a1 1 0 1 1 0-2h1.5v-5H12a1 1 0 1 1 0-2h1.5v-1A2.5 2.5 0 0 1 16 9zm0 2a.5.5 0 0 0-.5.5v1h1v-1A.5.5 0 0 0 16 11zm-.5 3.5v5h1v-5h-1zm0 6.5v1a.5.5 0 0 0 1 0v-1h-1z" fill="white"/>
  </svg>
);

/* ─── Sage logo SVG ─── */
const SageLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#00DC06"/>
    <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm4.5 13.5a.75.75 0 0 1-1.06 1.06L16 17.122l-3.44 3.44a.75.75 0 1 1-1.06-1.061L14.878 16l-3.44-3.44a.75.75 0 1 1 1.06-1.06L16 14.878l3.44-3.44a.75.75 0 1 1 1.06 1.061L17.122 16l3.44 3.44z" fill="white"/>
  </svg>
);

/* ─── Business Central logo SVG ─── */
const BCLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#0F6CBD"/>
    <path d="M8 9h16v3.2H8V9zm0 5.4h16v3.2H8v-3.2zm0 5.4h10.5v3.2H8V19.8z" fill="white"/>
  </svg>
);

/* ─── Keap logo SVG ─── */
const KeapLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="6" fill="#EA580C"/>
    <path d="M10 7h3.2v8.2L18.4 9h3.9l-5.9 6.9L22.6 25h-3.9l-4.4-6.9-1.1 1.3V25H10V7z" fill="white"/>
  </svg>
);

/* ─── Provider logo lookup (used by the connect-error toast) ─── */
const PROVIDER_LOGOS = {
  xero:            XeroLogo,
  quickbooks:      QBLogo,
  sage:            SageLogo,
  businesscentral: BCLogo,
  keap:            KeapLogo,
};

/* ─── Invoice HTML Generator ─── */
const generateInvoiceHTML = (invoice, extraCharge, editedEmail, extraDescription, logoDataUrl = null, paymentDetails = {}) => {
  const baseAmount = parseFloat(invoice.amountDue || invoice.totalAmounts || 0);
  const extraAmt   = parseFloat(extraCharge) || 0;
  const total      = baseAmount + extraAmt;
  const invoiceDate = formatDate(invoice.invoiceDates || new Date().toISOString());
  const address     = invoice.xeroContactAddress || {};
  const lineDescription = invoice.xeroDescription
    || `Invoice — ${invoice.xeroInvoiceNumber || invoice.invoiceNumbers || ""}`;

  const logoBlock = logoDataUrl
    ? `<img src="${logoDataUrl}" alt="Logo" style="max-height:110px;max-width:280px;display:block;object-fit:contain;" />`
    : `<div style="font-size:32px;font-weight:900;color:#0F172A;letter-spacing:-1px;line-height:1;">Invoice</div>`;

  const addrLines = [
    paymentDetails.companyName,
    ...(paymentDetails.address ? paymentDetails.address.split("\n") : []),
  ].filter(Boolean);

  const addrHTML = addrLines.length
    ? addrLines.map((line, i) =>
        i === 0
          ? `<div style="font-size:13px;font-weight:700;color:#0F172A;margin-bottom:3px;">${line}</div>`
          : `<div>${line}</div>`
      ).join("")
    : "";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 12px; background: #fff; color: #1a1a1a; padding: 40px 48px; }
  .layout { display: flex; justify-content: space-between; align-items: flex-start; gap: 40px; margin-bottom: 28px; }
  .col-left { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 20px; }
  .col-right { flex-shrink: 0; width: 270px; display: flex; flex-direction: column; gap: 20px; }
  .from-block { text-align: right; font-size: 12px; color: #444; line-height: 1.8; }
  h1.invoice-title { font-size: 28px; font-weight: 700; color: #0F172A; letter-spacing: .4px; margin-bottom: 12px; }
  .bill-to { font-size: 12px; color: #333; line-height: 1.85; }
  .bill-name { font-weight: 700; color: #0F172A; display: block; margin-bottom: 2px; }
  .bill-email { display: block; margin-top: 5px; color: #555; }
  .meta-list { display: flex; flex-direction: column; gap: 9px; padding-top: 4px; }
  .meta-label { font-size: 10px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 1px; }
  .meta-value { font-size: 12.5px; color: #0F172A; font-weight: 500; }
  .divider { border: none; border-top: 1px solid #E2E8F0; margin: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 24px; }
  thead tr { border-top: 2px solid #1a1a1a; border-bottom: 2px solid #1a1a1a; }
  thead th { padding: 8px 6px; text-align: left; font-size: 12px; font-weight: 700; color: #1a1a1a; }
  thead th.right { text-align: right; }
  tbody tr { border-bottom: 1px solid #e5e7eb; }
  tbody td { padding: 10px 6px; font-size: 12px; color: #333; }
  tbody td.right { text-align: right; }
  .totals-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
  .totals-table { width: 300px; border-collapse: collapse; }
  .totals-table td { padding: 5px 6px; font-size: 12px; color: #333; }
  .totals-table td.right { text-align: right; }
  .grand-row td { font-size: 13px; font-weight: 700; color: #1a1a1a; border-top: 2px solid #1a1a1a; padding-top: 7px; }
  .due-section { margin-top: 32px; border-top: 1px solid #e5e7eb; padding-top: 16px; }
  .due-date { font-size: 13px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px; }
  .note { font-size: 11px; color: #555; line-height: 1.6; margin-bottom: 6px; }
  .payment-block { margin-top: 12px; font-size: 11px; color: #333; line-height: 1.8; }
  .overdue-badge { display: inline-block; background: #FEF2F2; color: #991B1B; padding: 1px 7px; border-radius: 10px; font-size: 10px; font-weight: 700; margin-left: 6px; }
</style>
</head>
<body>
  <div class="layout">
    <div class="col-left">
      <div>${logoBlock}</div>
      <hr class="divider" />
      <div>
        <h1 class="invoice-title">TAX INVOICE</h1>
        <div class="bill-to">
          ${address.xeroContactName ? `<span class="bill-name">${address.xeroContactName}</span>` : ""}
          ${address.house     ? `<span>${address.house}</span><br/>`     : ""}
          ${address.street    ? `<span>${address.street}</span><br/>`    : ""}
          ${address.city      ? `<span>${address.city}</span><br/>`      : ""}
          ${address.region    ? `<span>${address.region}</span><br/>`    : ""}
          ${address.postalCode ? `<span>${address.postalCode}</span><br/>` : ""}
          ${address.country   ? `<span>${address.country}</span>`        : ""}
          ${(editedEmail || invoice.xeroContactEmail)
            ? `<span class="bill-email">${editedEmail || invoice.xeroContactEmail}</span>`
            : ""}
        </div>
      </div>
    </div>
    <div class="col-right">
      ${addrHTML ? `<div class="from-block">${addrHTML}</div>` : "<div></div>"}
      <hr class="divider" />
      <div class="meta-list">
        <div>
          <div class="meta-label">Invoice Date</div>
          <div class="meta-value">${invoiceDate}</div>
        </div>
        <div>
          <div class="meta-label">Currency</div>
          <div class="meta-value">${invoice.currency || "GBP"}</div>
        </div>
        <div>
          <div class="meta-label">Invoice Number</div>
          <div class="meta-value">${invoice.xeroInvoiceNumber || invoice.invoiceNumbers || "—"}</div>
        </div>
        <div>
          <div class="meta-label">Status</div>
          <div class="meta-value">${invoice.status || "—"}${isOverdue(invoice.dueDate) ? '<span class="overdue-badge">OVERDUE</span>' : ""}</div>
        </div>
      </div>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th style="width:60%">Description</th>
        <th class="right" style="width:15%">Quantity</th>
        <th class="right" style="width:25%">Amount ${invoice.currency || "GBP"}</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${lineDescription}</td>
        <td class="right">1.00</td>
        <td class="right">${baseAmount.toFixed(2)}</td>
      </tr>
      ${extraAmt > 0 ? `
      <tr>
        <td>${extraDescription || "Additional charge"}</td>
        <td class="right">1.00</td>
        <td class="right">${extraAmt.toFixed(2)}</td>
      </tr>` : ""}
    </tbody>
  </table>
  <div class="totals-wrap">
    <table class="totals-table">
      <tr><td>Subtotal</td><td class="right">${baseAmount.toFixed(2)}</td></tr>
      ${extraAmt > 0 ? `<tr><td>${extraDescription || "Additional charge"}</td><td class="right">${extraAmt.toFixed(2)}</td></tr>` : ""}
      <tr class="grand-row">
        <td>TOTAL ${invoice.currency || "GBP"}</td>
        <td class="right">${total.toFixed(2)}</td>
      </tr>
    </table>
  </div>
  <div class="due-section">
    <div class="due-date">Due Date: ${formatDate(invoice.dueDate)}${isOverdue(invoice.dueDate) ? '<span class="overdue-badge">OVERDUE</span>' : ""}</div>
    <p class="note">We reserve the right to charge interest on overdue amounts at 8% above the Bank of England base rate.</p>
    ${(paymentDetails.companyName || paymentDetails.accountNo || paymentDetails.sortCode || paymentDetails.payUrl) ? `
    <div class="payment-block">
      <strong style="display:block;margin-bottom:4px;color:#1a1a1a;">Please make payments to:</strong>
      ${paymentDetails.companyName ? `<span>${paymentDetails.companyName}</span><br/>` : ""}
      ${paymentDetails.accountNo   ? `<span>Account No - ${paymentDetails.accountNo}</span><br/>` : ""}
      ${paymentDetails.sortCode    ? `<span>Sort Code - ${paymentDetails.sortCode}</span><br/>` : ""}
      ${paymentDetails.payUrl      ? `<a href="${paymentDetails.payUrl}" style="color:#2563EB;">${paymentDetails.payUrl}</a>` : ""}
    </div>` : ""}
  </div>
</body>
</html>`;
};

/* ═══════════════════════════════════════════════════
   UPLOAD INVOICE MODAL
═══════════════════════════════════════════════════ */
const UploadInvoiceModal = ({ onClose, onUploaded, API_URL }) => {
  const [files, setFiles]           = useState([]);
  const [dragOver, setDragOver]     = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [results, setResults]       = useState([]);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState("");
  const fileInputRef = useRef(null);

  const getAuthToken = () => localStorage.getItem("authToken");

  const addFiles = (incoming) => {
    const pdfs = Array.from(incoming).filter(f => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length !== incoming.length) setError("Only PDF files are accepted.");
    else setError("");
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...pdfs.filter(f => !existing.has(f.name + f.size))];
    });
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true); setError("");
    const token = getAuthToken();
    const uploadResults = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append("invoice", file);
      try {
        const res  = await fetch(`${API_URL}/api/documents/invoices/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        uploadResults.push({ name: file.name, success: data.success, invoice: data.invoice, message: data.message });
      } catch (e) {
        uploadResults.push({ name: file.name, success: false, message: e.message || "Upload failed" });
      }
    }

    setResults(uploadResults);
    setUploading(false);
    setDone(true);
    const succeeded = uploadResults.filter(r => r.success).map(r => r.invoice).filter(Boolean);
    if (succeeded.length) onUploaded(succeeded);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #E2E8F0", boxShadow: "0 24px 80px rgba(0,0,0,0.18)", width: "100%", maxWidth: 540, display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAFBFD", borderRadius: "20px 20px 0 0" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
              <CloudUpload size={18} style={{ color: "#7C3AED" }} /> Upload invoices
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>Upload your own PDF invoices — same actions as Xero invoices</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <X size={14} style={{ color: "#64748B" }} />
          </button>
        </div>
        <div style={{ padding: 24, overflowY: "auto", display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
          {!done ? (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? "#7C3AED" : "#CBD5E1"}`, borderRadius: 14, padding: "32px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, cursor: "pointer", background: dragOver ? "#F5F3FF" : "#FAFBFD", transition: "all 0.15s", textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: dragOver ? "#EDE9FE" : "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}>
                  <Upload size={22} style={{ color: dragOver ? "#7C3AED" : "#94A3B8" }} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>{dragOver ? "Drop PDFs here" : "Click or drag & drop PDFs"}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>PDF files only · Multiple files supported</div>
              </div>
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" multiple style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
              {error && <div style={{ color: "#DC2626", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}><AlertCircle size={14} />{error}</div>}
              {files.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>{files.length} file{files.length !== 1 ? "s" : ""} selected</div>
                  {files.map((file, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: "#F5F3FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FileText size={15} style={{ color: "#7C3AED" }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>{(file.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #FECACA", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                        <X size={12} style={{ color: "#DC2626" }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>Upload complete</div>
              {results.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: r.success ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${r.success ? "#BBF7D0" : "#FECACA"}`, borderRadius: 10 }}>
                  {r.success ? <Check size={15} style={{ color: "#10B981", flexShrink: 0 }} /> : <AlertCircle size={15} style={{ color: "#DC2626", flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: r.success ? "#166534" : "#991B1B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                    {!r.success && <div style={{ fontSize: 11, color: "#DC2626" }}>{r.message}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid #F1F5F9", display: "flex", gap: 10, justifyContent: "flex-end", background: "#FAFBFD", borderRadius: "0 0 20px 20px" }}>
          {done ? (
            <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", border: "1px solid #7C3AED", background: "#7C3AED", color: "#fff" }}>
              <Check size={15} /> Done
            </button>
          ) : (
            <>
              <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", border: "1px solid #E2E8F0", background: "#fff", color: "#1E293B" }}>Cancel</button>
              <button onClick={handleUpload} disabled={!files.length || uploading} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", border: "1px solid #7C3AED", background: "#7C3AED", color: "#fff", opacity: (!files.length || uploading) ? 0.5 : 1 }}>
                {uploading ? <><span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Uploading…</> : <><Upload size={15} /> Upload {files.length > 0 ? `${files.length} file${files.length !== 1 ? "s" : ""}` : ""}</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   EMAIL DEFAULTS SETTINGS MODAL
═══════════════════════════════════════════════════ */
const EmailSettingsModal = ({ onClose, API_URL, onSaved }) => {
  const [logoDataUrl, setLogoDataUrl]       = useState(null);
  const [logoFileName, setLogoFileName]     = useState("");
  const [paymentDetails, setPaymentDetails] = useState({ companyName: "", address: "", accountNo: "", sortCode: "", payUrl: "" });
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");
  const logoInputRef = useRef(null);

  const getAuthToken = () => localStorage.getItem("authToken");

  useEffect(() => {
    const load = async () => {
      try {
        const res  = await fetch(`${API_URL}/api/users/email-settings`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        const data = await res.json();
        if (data.success) {
          if (data.logoDataUrl) { setLogoDataUrl(data.logoDataUrl); setLogoFileName("Saved logo"); }
          if (data.paymentDetails) setPaymentDetails(data.paymentDetails);
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [API_URL]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please upload an image file."); return; }
    if (file.size > 2 * 1024 * 1024) { setError("Logo must be under 2MB."); return; }
    setLogoFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoDataUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const res  = await fetch(`${API_URL}/api/users/email-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ logoDataUrl, paymentDetails }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSaved(true);
      onSaved({ logoDataUrl, paymentDetails });
      setTimeout(() => { setSaved(false); onClose(); }, 1000);
    } catch (e) {
      setError(e.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #E2E8F0", boxShadow: "0 24px 80px rgba(0,0,0,0.18)", width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAFBFD", borderRadius: "20px 20px 0 0" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
              <Settings size={18} style={{ color: "#2563EB" }} /> Email defaults
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>Saved logo and payment details are pre-filled on every invoice email</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <X size={14} style={{ color: "#64748B" }} />
          </button>
        </div>
        <div style={{ padding: 24, overflowY: "auto", display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
              <span style={{ width: 20, height: 20, border: "2px solid #E2E8F0", borderTopColor: "#2563EB", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
            </div>
          ) : (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Company logo</div>
                {logoDataUrl ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12 }}>
                    <img src={logoDataUrl} alt="Logo" style={{ maxHeight: 56, maxWidth: 160, objectFit: "contain" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{logoFileName}</div>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>Shown at the top of every invoice</div>
                    </div>
                    <button onClick={() => { setLogoDataUrl(null); setLogoFileName(""); if (logoInputRef.current) logoInputRef.current.value = ""; }}
                      style={{ padding: "5px 10px", background: "#fff", border: "1px solid #FECACA", borderRadius: 7, cursor: "pointer", fontSize: 12, color: "#DC2626", display: "flex", alignItems: "center", gap: 4 }}>
                      <X size={12} /> Remove
                    </button>
                  </div>
                ) : (
                  <div onClick={() => logoInputRef.current?.click()}
                    style={{ border: "1.5px dashed #CBD5E1", borderRadius: 12, padding: "20px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer", background: "#FAFBFD", textAlign: "center" }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FileText size={18} style={{ color: "#94A3B8" }} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>Click to upload logo</div>
                    <div style={{ fontSize: 12, color: "#94A3B8" }}>PNG · JPG · SVG — max 2MB</div>
                  </div>
                )}
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoUpload} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Payment details</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input type="text" placeholder="Company name" value={paymentDetails.companyName} onChange={e => setPaymentDetails(p => ({ ...p, companyName: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#0F172A", outline: "none", boxSizing: "border-box" }} />
                  <textarea placeholder="Company address" value={paymentDetails.address} rows={2} onChange={e => setPaymentDetails(p => ({ ...p, address: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#0F172A", outline: "none", boxSizing: "border-box", resize: "vertical" }} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <input type="text" placeholder="Account No" value={paymentDetails.accountNo} onChange={e => setPaymentDetails(p => ({ ...p, accountNo: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#0F172A", outline: "none", boxSizing: "border-box" }} />
                    <input type="text" placeholder="Sort Code" value={paymentDetails.sortCode} onChange={e => setPaymentDetails(p => ({ ...p, sortCode: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#0F172A", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <input type="url" placeholder="Payment URL" value={paymentDetails.payUrl} onChange={e => setPaymentDetails(p => ({ ...p, payUrl: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#0F172A", outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>
              {(paymentDetails.companyName || paymentDetails.accountNo || paymentDetails.sortCode || paymentDetails.payUrl) && (
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "12px 14px", fontSize: 12, color: "#334155", lineHeight: 1.8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>Preview on invoice</div>
                  <strong>Please make payments to:</strong><br />
                  {paymentDetails.companyName && <>{paymentDetails.companyName}<br /></>}
                  {paymentDetails.accountNo   && <>Account No - {paymentDetails.accountNo}<br /></>}
                  {paymentDetails.sortCode    && <>Sort Code - {paymentDetails.sortCode}<br /></>}
                  {paymentDetails.payUrl      && <span style={{ color: "#2563EB" }}>{paymentDetails.payUrl}</span>}
                </div>
              )}
              {error && <div style={{ color: "#DC2626", fontSize: 13 }}>{error}</div>}
            </>
          )}
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid #F1F5F9", display: "flex", gap: 10, justifyContent: "flex-end", background: "#FAFBFD", borderRadius: "0 0 20px 20px" }}>
          <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", border: "1px solid #E2E8F0", background: "#fff", color: "#1E293B" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || loading} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", border: "1px solid #1E40AF", background: saved ? "#10B981" : "#1E40AF", color: "#fff", opacity: saving ? 0.7 : 1, transition: "background 0.2s" }}>
            {saved ? <><Check size={15} /> Saved!</> : saving ? "Saving…" : <><Check size={15} /> Save defaults</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   SEND EMAIL MODAL
═══════════════════════════════════════════════════ */
const SendEmailModal = ({ invoice, onClose, onSend, defaultLogo, defaultPaymentDetails }) => {
  const [step, setStep]               = useState("charge");
  const [extraCharge, setExtraCharge] = useState("");
  const [extraDescription, setExtraDescription] = useState("Late payment fee");
  const [editedEmail, setEditedEmail] = useState(invoice.xeroContactEmail || "");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState(` Pending Invoice ${invoice.xeroInvoiceNumber} — Payment Required`);
  const [emailBody, setEmailBody] = useState(
    `Dear ${invoice.xeroContactName || "Customer"},\n\nPlease find attached your invoice ${invoice.xeroInvoiceNumber}${isOverdue(invoice.dueDate) ? ", which is now overdue" : ""}.\n\nAmount due: ${formatCurrency(invoice.amountDue, invoice.currency)}\nDue date: ${formatDate(invoice.dueDate)}\n\nPlease arrange payment at your earliest convenience.\n\nKind regards\n\n6EL∆ TEAM`
  );
  const [sending, setSending]         = useState(false);
  const [error, setError]             = useState("");

  const [logoDataUrl, setLogoDataUrl]       = useState(defaultLogo || null);
  const [logoFileName, setLogoFileName]     = useState(defaultLogo ? "Saved logo" : "");
  const [paymentDetails, setPaymentDetails] = useState(defaultPaymentDetails || { companyName: "", address: "", accountNo: "", sortCode: "", payUrl: "" });
  const [showPaymentDetails, setShowPaymentDetails] = useState(
    !!(defaultPaymentDetails?.companyName || defaultPaymentDetails?.address || defaultPaymentDetails?.accountNo || defaultPaymentDetails?.sortCode || defaultPaymentDetails?.payUrl)
  );

  const logoInputRef = useRef(null);
  const isFutureDue  = invoice.dueDate && new Date(invoice.dueDate) > new Date();
  const overdue      = isOverdue(invoice.dueDate);
  const baseAmt = invoice.amountDue
    || parseFloat(String(invoice.totalAmounts || "0").replace(/,/g, "")) || 0;
  const total = baseAmt + (parseFloat(extraCharge) || 0);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please upload an image file."); return; }
    if (file.size > 2 * 1024 * 1024) { setError("Logo must be under 2MB."); return; }
    setLogoFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoDataUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => { setLogoDataUrl(null); setLogoFileName(""); if (logoInputRef.current) logoInputRef.current.value = ""; };

  const updateBodyWithCharge = (charge) => {
    const chargeNote = parseFloat(charge) > 0 ? `\nAdditional charge: ${formatCurrency(parseFloat(charge), invoice.currency)} (${extraDescription})` : "";
    setEmailBody(`Dear ${invoice.xeroContactName || "Customer"},\n\nPlease find attached your invoice ${invoice.xeroInvoiceNumber}${overdue ? ", which is now overdue" : ""}.\n\nAmount due: ${formatCurrency(invoice.amountDue, invoice.currency)}${chargeNote}\nTotal: ${formatCurrency((invoice.amountDue || 0) + (parseFloat(charge) || 0), invoice.currency)}\nDue date: ${formatDate(invoice.dueDate)}\n\nPlease arrange payment at your earliest convenience.\n\nKind regards`);
  };

  const handleSend = async () => {
    if (!editedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedEmail)) {
      setError("Please enter a valid email address."); return;
    }
    setSending(true); setStep("sending");
    try {
      await onSend({ invoice, extraCharge, extraDescription, editedEmail, emailSubject, emailBody, logoDataUrl, paymentDetails });
      setStep("done");
    } catch (e) {
      setError(e.message || "Failed to send email."); setStep("compose");
    } finally {
      setSending(false);
    }
  };

  const invoicePreviewHTML = generateInvoiceHTML(invoice, extraCharge, editedEmail, extraDescription, logoDataUrl, paymentDetails);
  const inputStyle = { width: "100%", padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontFamily: "DM Sans, sans-serif", fontSize: 14, color: "#0F172A", outline: "none", boxSizing: "border-box" };

  return (
    <>
      <style>{`
        .sem-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:24px; }
        .sem-modal { background:#fff;border-radius:20px;border:1px solid #E2E8F0;box-shadow:0 24px 80px rgba(0,0,0,0.18);width:100%;max-width:640px;display:flex;flex-direction:column;max-height:90vh;overflow:hidden; }
        .sem-modal.wide { max-width:900px; }
        .sem-header { padding:20px 24px 16px;border-bottom:1px solid #F1F5F9;display:flex;align-items:center;justify-content:space-between;background:#FAFBFD;border-radius:20px 20px 0 0; }
        .sem-body { padding:24px;overflow-y:auto;display:flex;flex-direction:column;gap:18px;flex:1; }
        .sem-footer { padding:16px 24px;border-top:1px solid #F1F5F9;display:flex;gap:10px;justify-content:flex-end;background:#FAFBFD;border-radius:0 0 20px 20px; }
        .sem-label { font-size:12px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px; }
        .sem-btn { display:inline-flex;align-items:center;gap:6px;padding:10px 20px;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;cursor:pointer;border:1px solid;transition:all .15s; }
        .sem-btn-primary { background:#1E40AF;color:#fff;border-color:#1E40AF; }
        .sem-btn-primary:hover { background:#1d3a9e; }
        .sem-btn-primary:disabled { opacity:.5;cursor:not-allowed; }
        .sem-btn-secondary { background:#fff;color:#1E293B;border-color:#E2E8F0; }
        .sem-btn-secondary:hover { border-color:#2563EB;background:#EFF6FF;color:#2563EB; }
        .sem-summary-card { background:#F8FAFC;border:1px solid #E2E8F0;border-radius:12px;padding:16px 20px; }
        .sem-summary-row { display:flex;justify-content:space-between;align-items:center;padding:5px 0;font-size:13px; }
        .sem-summary-row.total { font-size:15px;font-weight:700;border-top:1px solid #E2E8F0;padding-top:10px;margin-top:6px;color:#0F172A; }
        .sem-alert { display:flex;gap:10px;padding:12px 16px;border-radius:10px;font-size:13px; }
        .sem-alert-overdue { background:#FEF2F2;border:1px solid #FECACA;color:#991B1B; }
        .sem-alert-future  { background:#EFF6FF;border:1px solid #BFDBFE;color:#1E40AF; }
        .sem-step-indicator { display:flex;gap:6px;align-items:center; }
        .sem-step { width:6px;height:6px;border-radius:50%;background:#E2E8F0;transition:background .2s; }
        .sem-step.active { background:#2563EB;width:18px;border-radius:3px; }
        .sem-step.done   { background:#10B981; }
        @keyframes pulse-dot { 0%,100%{opacity:1}50%{opacity:.3} }
        .sem-sending-dot { display:inline-block;width:8px;height:8px;border-radius:50%;background:#2563EB;animation:pulse-dot 1.2s ease-in-out infinite; }
        .sem-sending-dot:nth-child(2){animation-delay:.2s} .sem-sending-dot:nth-child(3){animation-delay:.4s}
        @keyframes checkmark { 0%{transform:scale(0);opacity:0} 100%{transform:scale(1);opacity:1} }
        .sem-success-icon { animation:checkmark .4s ease-out forwards; }
        .sem-logo-dropzone { border:1.5px dashed #CBD5E1;border-radius:12px;padding:16px;display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;background:#FAFBFD;text-align:center; }
        .sem-logo-dropzone:hover { border-color:#2563EB;background:#EFF6FF; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .spinner { width:20px;height:20px;border:2px solid #E2E8F0;border-top-color:#2563EB;border-radius:50%;animation:spin .7s linear infinite; }
      `}</style>
      <div className="sem-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className={`sem-modal${step === "preview" ? " wide" : ""}`}>
          <div className="sem-header">
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
                <Mail size={18} style={{ color: "#2563EB" }} />
                {step === "charge" && "Prepare invoice"}
                {step === "compose" && "Compose email"}
                {step === "preview" && "Preview invoice"}
                {step === "sending" && "Sending…"}
                {step === "done" && "Email sent"}
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{invoice.xeroInvoiceNumber} · {invoice.xeroContactName}</div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div className="sem-step-indicator">
                {["charge","compose","preview"].map((s, i) => (
                  <div key={s} className={`sem-step ${step === s ? "active" : (["charge","compose","preview"].indexOf(step) > i || step === "done" || step === "sending") ? "done" : ""}`} />
                ))}
              </div>
              <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <X size={14} style={{ color: "#64748B" }} />
              </button>
            </div>
          </div>

          {step === "charge" && (
            <>
              <div className="sem-body">
                {overdue && (
                  <div className="sem-alert sem-alert-overdue">
                    <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>This invoice is <strong>overdue</strong> (due {formatDate(invoice.dueDate)}). You can add a late payment fee below.</span>
                  </div>
                )}
                {isFutureDue && (
                  <div className="sem-alert sem-alert-future">
                    <Clock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>Due date is <strong>{formatDate(invoice.dueDate)}</strong>. You can still add optional charges before sending.</span>
                  </div>
                )}
                <div>
                  <div className="sem-label">Original invoice amount</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#0F172A", fontFamily: "'DM Mono', monospace" }}>{formatCurrency(baseAmt, invoice.currency)}</div>
                </div>
                {invoice.xeroDescription && (
                  <div>
                    <div className="sem-label">Invoice description</div>
                    <div style={{ padding: "10px 14px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 13, color: "#334155", lineHeight: 1.6 }}>{invoice.xeroDescription}</div>
                  </div>
                )}
                <div>
                  <div className="sem-label">Extra charge (optional)</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ padding: "10px 14px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px 0 0 10px", fontSize: 14, color: "#64748B", fontWeight: 600 }}>{invoice.currency || "GBP"}</span>
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={extraCharge} onChange={(e) => setExtraCharge(e.target.value)} style={{ ...inputStyle, borderRadius: "0 10px 10px 0", borderLeft: "none", width: 140 }} />
                  </div>
                </div>
                {parseFloat(extraCharge) > 0 && (
                  <div>
                    <div className="sem-label">Charge description</div>
                    <input type="text" style={inputStyle} value={extraDescription} onChange={(e) => setExtraDescription(e.target.value)} placeholder="e.g. Late payment fee" />
                  </div>
                )}
                <div className="sem-summary-card">
                  <div className="sem-summary-row">
                    <span style={{ color: "#64748B" }}>Original amount</span>
                    <span style={{ fontFamily: "'DM Mono', monospace" }}>{formatCurrency(invoice.amountDue, invoice.currency)}</span>
                  </div>
                  {parseFloat(extraCharge) > 0 && (
                    <div className="sem-summary-row">
                      <span style={{ color: "#64748B" }}>{extraDescription || "Extra charge"}</span>
                      <span style={{ fontFamily: "'DM Mono', monospace", color: "#D97706" }}>+{formatCurrency(parseFloat(extraCharge), invoice.currency)}</span>
                    </div>
                  )}
                  <div className="sem-summary-row total">
                    <span>Total to send</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", color: "#2563EB" }}>{formatCurrency(total, invoice.currency)}</span>
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div className="sem-label" style={{ marginBottom: 0 }}>Company logo</div>
                    {logoDataUrl && <span style={{ fontSize: 11, color: "#10B981", fontWeight: 600 }}>✓ From defaults</span>}
                  </div>
                  {logoDataUrl ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12 }}>
                      <img src={logoDataUrl} alt="Logo" style={{ maxHeight: 48, maxWidth: 140, objectFit: "contain" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{logoFileName}</div>
                      </div>
                      <button onClick={removeLogo} style={{ padding: "5px 10px", background: "#fff", border: "1px solid #FECACA", borderRadius: 7, cursor: "pointer", fontSize: 12, color: "#DC2626", display: "flex", alignItems: "center", gap: 4 }}>
                        <X size={12} /> Remove
                      </button>
                    </div>
                  ) : (
                    <div className="sem-logo-dropzone" onClick={() => logoInputRef.current?.click()}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FileText size={16} style={{ color: "#94A3B8" }} />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>Click to upload logo</div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>PNG · JPG · SVG</div>
                    </div>
                  )}
                  <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoUpload} />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div className="sem-label" style={{ marginBottom: 0 }}>Payment details</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {showPaymentDetails && <span style={{ fontSize: 11, color: "#10B981", fontWeight: 600 }}>✓ From defaults</span>}
                      <button onClick={() => setShowPaymentDetails(v => !v)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", background: showPaymentDetails ? "#EFF6FF" : "#fff", border: "1px solid", borderColor: showPaymentDetails ? "#BFDBFE" : "#E2E8F0", borderRadius: 8, fontSize: 12, fontWeight: 600, color: showPaymentDetails ? "#1E40AF" : "#64748B", cursor: "pointer" }}>
                        {showPaymentDetails ? <><Check size={12} /> Shown</> : <><Plus size={12} /> Show</>}
                      </button>
                    </div>
                  </div>
                  {showPaymentDetails && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <input type="text" placeholder="Company name" value={paymentDetails.companyName} onChange={e => setPaymentDetails(p => ({ ...p, companyName: e.target.value }))} style={inputStyle} />
                      <textarea placeholder="Company address" value={paymentDetails.address} rows={2} onChange={e => setPaymentDetails(p => ({ ...p, address: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <input type="text" placeholder="Account No" value={paymentDetails.accountNo} onChange={e => setPaymentDetails(p => ({ ...p, accountNo: e.target.value }))} style={inputStyle} />
                        <input type="text" placeholder="Sort Code" value={paymentDetails.sortCode} onChange={e => setPaymentDetails(p => ({ ...p, sortCode: e.target.value }))} style={inputStyle} />
                      </div>
                      <input type="url" placeholder="Payment URL" value={paymentDetails.payUrl} onChange={e => setPaymentDetails(p => ({ ...p, payUrl: e.target.value }))} style={inputStyle} />
                    </div>
                  )}
                </div>
              </div>
              <div className="sem-footer">
                <button className="sem-btn sem-btn-secondary" onClick={onClose}>Cancel</button>
                <button className="sem-btn sem-btn-primary" onClick={() => { updateBodyWithCharge(extraCharge); setStep("compose"); }}>Continue <ChevronRight size={15} /></button>
              </div>
            </>
          )}

          {step === "compose" && (
            <>
              <div className="sem-body">
                <div>
                  <div className="sem-label">Recipient email</div>
                  {isEditingEmail ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input type="email" style={inputStyle} value={editedEmail} onChange={(e) => setEditedEmail(e.target.value)} autoFocus />
                      <button onClick={() => setIsEditingEmail(false)} style={{ padding: "10px 14px", background: "#10B981", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600 }}>
                        <Check size={14} /> Save
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, padding: "10px 14px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 14, color: "#0F172A" }}>{editedEmail || <span style={{ color: "#94A3B8" }}>No email</span>}</div>
                      <button onClick={() => setIsEditingEmail(true)} style={{ padding: "8px 12px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748B" }}>
                        <Edit2 size={13} /> Edit
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <div className="sem-label">Subject</div>
                  <input type="text" style={inputStyle} value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                </div>
                <div>
                  <div className="sem-label">Message body</div>
                  <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 140 }} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                </div>
                <div className="sem-summary-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>Invoice attached</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginTop: 2 }}>{invoice.xeroInvoiceNumber}.pdf</div>
                    {invoice.xeroDescription && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, fontStyle: "italic" }}>"{invoice.xeroDescription.length > 60 ? invoice.xeroDescription.slice(0, 60) + "…" : invoice.xeroDescription}"</div>}
                    {parseFloat(extraCharge) > 0 && <div style={{ fontSize: 12, color: "#D97706", marginTop: 2 }}>Includes extra charge: +{formatCurrency(parseFloat(extraCharge), invoice.currency)}</div>}
                    {logoDataUrl && <div style={{ fontSize: 12, color: "#10B981", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><Check size={11} /> Logo included</div>}
                    {invoice._isUploaded && <div style={{ fontSize: 12, color: "#7C3AED", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><Upload size={11} /> Manually uploaded invoice</div>}
                    {invoice._isQuickBooks && <div style={{ fontSize: 12, color: "#15803D", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><QBLogo size={11} /> QuickBooks invoice</div>}
                    {invoice._isSage && <div style={{ fontSize: 12, color: "#15803D", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><SageLogo size={11} /> Sage invoice</div>}
                    {invoice._isBusinessCentral && <div style={{ fontSize: 12, color: "#0F6CBD", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><BCLogo size={11} /> Business Central invoice</div>}
                    {invoice._isKeap && <div style={{ fontSize: 12, color: "#EA580C", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><KeapLogo size={11} /> Keap invoice</div>}
                  </div>
                  <button className="sem-btn sem-btn-secondary" style={{ fontSize: 12 }} onClick={() => setStep("preview")}>
                    <Eye size={14} /> Preview
                  </button>
                </div>
                {error && <div style={{ color: "#DC2626", fontSize: 13 }}>{error}</div>}
              </div>
              <div className="sem-footer">
                <button className="sem-btn sem-btn-secondary" onClick={() => setStep("charge")}><ChevronLeft size={15} /> Back</button>
                <button className="sem-btn sem-btn-primary" onClick={handleSend} disabled={sending}><Send size={15} /> Send email</button>
              </div>
            </>
          )}

          {step === "preview" && (
            <>
              <div className="sem-body" style={{ padding: 16 }}>
                <iframe srcDoc={invoicePreviewHTML} style={{ width: "100%", height: "60vh", border: "1px solid #E2E8F0", borderRadius: 10 }} title="Invoice preview" />
              </div>
              <div className="sem-footer">
                <button className="sem-btn sem-btn-secondary" onClick={() => setStep("compose")}><ChevronLeft size={15} /> Back to compose</button>
                <button className="sem-btn sem-btn-primary" onClick={handleSend} disabled={sending}><Send size={15} /> Send email</button>
              </div>
            </>
          )}

          {step === "sending" && (
            <div className="sem-body" style={{ alignItems: "center", justifyContent: "center", padding: "60px 24px", gap: 20 }}>
              <div style={{ display: "flex", gap: 6 }}>
                <span className="sem-sending-dot" /><span className="sem-sending-dot" /><span className="sem-sending-dot" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Sending your email…</div>
              <div style={{ fontSize: 13, color: "#64748B" }}>Generating invoice and delivering to {editedEmail}</div>
            </div>
          )}

          {step === "done" && (
            <>
              <div className="sem-body" style={{ alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: 16 }}>
                <div className="sem-success-icon" style={{ width: 64, height: 64, borderRadius: "50%", background: "#F0FDF4", border: "2px solid #86EFAC", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Check size={28} style={{ color: "#10B981" }} />
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>Email sent!</div>
                <div style={{ fontSize: 13, color: "#64748B", textAlign: "center", maxWidth: 320 }}>
                  Invoice <strong>{invoice.xeroInvoiceNumber}</strong> · <strong>{formatCurrency(total, invoice.currency)}</strong> sent to <strong>{editedEmail}</strong>.
                </div>
              </div>
              <div className="sem-footer" style={{ justifyContent: "center" }}>
                <button className="sem-btn sem-btn-primary" onClick={onClose}>Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const XeroInvoices = () => {
  const navigate = useNavigate();
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [invoices, setInvoices]               = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [isLoading, setIsLoading]             = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [previewUrl, setPreviewUrl]           = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [accessMessage, setAccessMessage]     = useState("");

  // ── Xero invoice-date range ──
  // rangeFrom/rangeTo are what's in the date inputs; appliedRange is what the
  // list is currently filtered by (null = no range, show everything).
  const [xeroConnected, setXeroConnected]     = useState(false);
  // False until the first /api/xero/status call settles, so the "Connect Xero"
  // state doesn't flash for accounts that are already connected.
  const [xeroStatusChecked, setXeroStatusChecked] = useState(false);
  const [rangeFrom, setRangeFrom]             = useState("");
  const [rangeTo, setRangeTo]                 = useState("");
  const [appliedRange, setAppliedRange]       = useState(null);
  const [rangeError, setRangeError]           = useState("");
  const [isSyncingXero, setIsSyncingXero]     = useState(false);
  const [xeroSyncMessage, setXeroSyncMessage] = useState("");
  const [xeroSyncError, setXeroSyncError]     = useState("");
  const appliedRangeRef = useRef(null);
  const [searchQuery, setSearchQuery]         = useState("");
  const [currentPage, setCurrentPage]         = useState(1);
  const [itemsPerPage]                        = useState(12);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [isUpdatePaymentOpen, setUpdatePaymentOpen] = useState(false);
  const [isCheckingStripe, setIsCheckingStripe] = useState(true);
  const [pendingInvoice, setPendingInvoice]   = useState(null);
  const [sendEmailInvoice, setSendEmailInvoice] = useState(null);

  const [activeTab, setActiveTab]             = useState("xero");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadedInvoices, setUploadedInvoices] = useState([]);

  const [qbInvoices, setQbInvoices]           = useState([]);
  const [qbConnected, setQbConnected]         = useState(false);
  const [isLoadingQB, setIsLoadingQB]         = useState(false);
  const [isSyncingQB, setIsSyncingQB]         = useState(false);
  const [qbSyncMessage, setQbSyncMessage]     = useState("");
  const [qbSyncError, setQbSyncError]         = useState("");

  const [sageInvoices, setSageInvoices]       = useState([]);
  const [sageConnected, setSageConnected]     = useState(false);
  const [isLoadingSage, setIsLoadingSage]     = useState(false);
  const [isSyncingSage, setIsSyncingSage]     = useState(false);
  const [sageSyncMessage, setSageSyncMessage] = useState("");
  const [sageSyncError, setSageSyncError]     = useState("");

  const [bcInvoices, setBcInvoices]           = useState([]);
  const [bcConnected, setBcConnected]         = useState(false);
  const [isLoadingBC, setIsLoadingBC]         = useState(false);
  const [isSyncingBC, setIsSyncingBC]         = useState(false);
  const [bcSyncMessage, setBcSyncMessage]     = useState("");
  const [bcSyncError, setBcSyncError]         = useState("");

  const [keapInvoices, setKeapInvoices]       = useState([]);
  const [keapConnected, setKeapConnected]     = useState(false);
  const [isLoadingKeap, setIsLoadingKeap]     = useState(false);
  const [isSyncingKeap, setIsSyncingKeap]     = useState(false);
  const [keapSyncMessage, setKeapSyncMessage] = useState("");
  const [keapSyncError, setKeapSyncError]     = useState("");

  // ── Connect-error toast: { provider, label, message } | null.
  // Set when a provider's /connect endpoint responds without a redirect URL
  // (e.g. { success: false, message: "Only company owners can connect Keap" }).
  const [connectError, setConnectError]       = useState(null);
  const [connectingProvider, setConnectingProvider] = useState(null);

  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [defaultLogo, setDefaultLogo]             = useState(null);
  const [defaultPaymentDetails, setDefaultPaymentDetails] = useState({ companyName: "", address: "", accountNo: "", sortCode: "", payUrl: "" });
  const [paystackAccountStatus, setPaystackAccountStatus] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const user    = getCurrentUser();

  const closeNavigationDropdown = useCallback(() => setNavigationDropdownOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);

  const getAuthToken = () => {
    const token = localStorage.getItem("authToken");
    if (!token) { alert("Please log in again"); return null; }
    return token;
  };

  // Auto-dismiss the connect-error toast after 8s.
  useEffect(() => {
    if (!connectError) return;
    const timer = setTimeout(() => setConnectError(null), 8000);
    return () => clearTimeout(timer);
  }, [connectError]);

  // ── Shared connect flow for QuickBooks / Sage / Business Central / Keap.
  // Redirects when the backend returns a URL; otherwise surfaces the backend's
  // own message in the toast instead of failing silently.
  const startProviderConnect = async (provider, apiPath, label) => {
    if (connectingProvider) return;
    setConnectError(null);
    setConnectingProvider(provider);
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/${apiPath}/connect`, { headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json().catch(() => null);
      if (data?.url) { window.location.href = data.url; return; }
      setConnectError({
        provider,
        label,
        message: data?.message || `We couldn't start the ${label} connection. Please try again.`,
      });
    } catch {
      setConnectError({
        provider,
        label,
        message: `We couldn't reach the server to connect ${label}. Please check your connection and try again.`,
      });
    } finally {
      setConnectingProvider(null);
    }
  };

  useEffect(() => {
    const loadEmailSettings = async () => {
      try {
        const token = getAuthToken();
        const res   = await fetch(`${API_URL}/api/users/email-settings`, { headers: { Authorization: `Bearer ${token}` } });
        const data  = await res.json();
        if (data.success) {
          if (data.logoDataUrl)    setDefaultLogo(data.logoDataUrl);
          if (data.paymentDetails) setDefaultPaymentDetails(data.paymentDetails);
        }
      } catch {}
    };
    loadEmailSettings();
  }, [API_URL]);

  useEffect(() => {
    const loadUploadedInvoices = async () => {
      try {
        const token = getAuthToken();
        const res   = await fetch(`${API_URL}/api/documents/invoices/uploaded`, { headers: { Authorization: `Bearer ${token}` } });
        const data  = await res.json();
        if (data.success && data.invoices) {
          setUploadedInvoices(data.invoices.map(inv => ({ ...inv, _isUploaded: true })));
        }
      } catch {}
    };
    loadUploadedInvoices();
  }, [API_URL]);

  useEffect(() => {
    const loadQB = async () => {
      try {
        const token = getAuthToken();
        const statusRes  = await fetch(`${API_URL}/api/quickbooks/status`, { headers: { Authorization: `Bearer ${token}` } });
        const statusData = await statusRes.json();
        setQbConnected(!!(statusData.success && statusData.connected));
        if (statusData.connected) {
          setIsLoadingQB(true);
          const invRes  = await fetch(`${API_URL}/api/quickbooks/invoices`, { headers: { Authorization: `Bearer ${token}` } });
          const invData = await invRes.json();
          if (invData.success) {
            setQbInvoices((invData.invoices || []).map(inv => ({ ...inv, _isQuickBooks: true })));
          }
        }
      } catch {}
      finally { setIsLoadingQB(false); }
    };
    loadQB();
  }, [API_URL]);

  useEffect(() => {
    const loadSage = async () => {
      try {
        const token      = getAuthToken();
        const statusRes  = await fetch(`${API_URL}/api/sage/status`, { headers: { Authorization: `Bearer ${token}` } });
        const statusData = await statusRes.json();
        setSageConnected(!!(statusData.success && statusData.connected));
        if (statusData.connected) {
          setIsLoadingSage(true);
          const invRes  = await fetch(`${API_URL}/api/sage/invoices`, { headers: { Authorization: `Bearer ${token}` } });
          const invData = await invRes.json();
          if (invData.success) {
            setSageInvoices((invData.invoices || []).map(inv => ({ ...inv, _isSage: true })));
          }
        }
      } catch {}
      finally { setIsLoadingSage(false); }
    };
    loadSage();
  }, [API_URL]);

  useEffect(() => {
    const loadBC = async () => {
      try {
        const token      = getAuthToken();
        const statusRes  = await fetch(`${API_URL}/api/business-central/status`, { headers: { Authorization: `Bearer ${token}` } });
        const statusData = await statusRes.json();
        setBcConnected(!!(statusData.success && statusData.connected));
        if (statusData.connected) {
          setIsLoadingBC(true);
          const invRes  = await fetch(`${API_URL}/api/business-central/invoices`, { headers: { Authorization: `Bearer ${token}` } });
          const invData = await invRes.json();
          if (invData.success) {
            setBcInvoices((invData.invoices || []).map(inv => ({ ...inv, _isBusinessCentral: true })));
          }
        }
      } catch {}
      finally { setIsLoadingBC(false); }
    };
    loadBC();
  }, [API_URL]);

  useEffect(() => {
    const loadKeap = async () => {
      try {
        const token      = getAuthToken();
        const statusRes  = await fetch(`${API_URL}/api/keap/status`, { headers: { Authorization: `Bearer ${token}` } });
        const statusData = await statusRes.json();
        setKeapConnected(!!(statusData.success && statusData.connected));
        if (statusData.connected) {
          setIsLoadingKeap(true);
          const invRes  = await fetch(`${API_URL}/api/keap/invoices`, { headers: { Authorization: `Bearer ${token}` } });
          const invData = await invRes.json();
          if (invData.success) {
            setKeapInvoices((invData.invoices || []).map(inv => ({ ...inv, _isKeap: true })));
          }
        }
      } catch {}
      finally { setIsLoadingKeap(false); }
    };
    loadKeap();
  }, [API_URL]);

  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        setIsCheckingStripe(true);
        const stripeData = await stripeService.getAccountStatus();
        setStripeAccountStatus(stripeData);
      } catch {
        setStripeAccountStatus({ connected: false, onboardingComplete: false });
      } finally {
        setIsCheckingStripe(false);
      }
    };
    fetchStripeStatus();
  }, []);

  useEffect(() => {
    const fetchPaystackStatus = async () => {
      try {
        const data = await paystackService.getAccountStatus();
        setPaystackAccountStatus(data);
      } catch {
        setPaystackAccountStatus({ connected: false, onboardingComplete: false, isActiveProvider: false });
      }
    };
    fetchPaystackStatus();
  }, []);

  const isAnyProviderReady =
    (stripeAccountStatus?.connected && stripeAccountStatus?.onboardingComplete && stripeAccountStatus?.isActiveProvider !== false) ||
    (paystackAccountStatus?.connected && paystackAccountStatus?.isActiveProvider);

  const baseList = activeTab === "xero"
    ? invoices
    : activeTab === "uploaded"
      ? uploadedInvoices
      : activeTab === "quickbooks"
        ? qbInvoices
        : activeTab === "businesscentral"
          ? bcInvoices
          : activeTab === "keap"
            ? keapInvoices
            : sageInvoices;

  // Xero is filtered by the server when a range is applied. The other
  // accounting tabs load their full list, so the range is applied here.
  // Uploaded invoices are never range-filtered.
  const isClientRangeTab = ["quickbooks", "sage", "businesscentral", "keap"].includes(activeTab);
  const activeList = useMemo(() => {
    if (!isClientRangeTab || !appliedRange) return baseList;
    return baseList.filter((inv) => {
      const d = getInvoiceDateValue(inv);
      return d && d >= appliedRange.from && d <= appliedRange.to;
    });
  }, [baseList, isClientRangeTab, appliedRange]);

  useEffect(() => {
    if (!searchQuery.trim()) { setFilteredInvoices(activeList); setCurrentPage(1); return; }
    const q = searchQuery.toLowerCase().trim();
    setFilteredInvoices(activeList.filter((inv) =>
      [inv.xeroInvoiceNumber, inv.xeroContactName, inv.xeroContactEmail, inv.status, inv.amountDue?.toString(), inv.xeroDescription]
        .some((f) => f?.toLowerCase().includes(q)) ||
      (inv.dueDate && new Date(inv.dueDate).toLocaleDateString("en-GB").includes(q))
    ));
    setCurrentPage(1);
  }, [searchQuery, activeList]);

  // Loads Xero invoices from our database. `range` ({ from, to } as
  // "YYYY-MM-DD", or null for no filter) defaults to the applied range.
  const fetchInvoices = useCallback(async (range = appliedRangeRef.current) => {
    setIsLoading(true); setAccessMessage("");
    try {
      const token    = getAuthToken();
      const query    = range?.from && range?.to
        ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : "";
      const response = await fetch(`${API_URL}/api/xero/invoices${query}`, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
      const data     = await response.json();
      if (data.success) {
        const withPdfs = (data.invoices || []).filter((inv) => inv.xeroPdfS3Key);
        setInvoices(withPdfs);
        // With a range applied, an empty result isn't an access problem — the
        // range-specific empty state below handles it.
        if (query) { /* no access message */ }
        else if (!withPdfs.length && data.invoices?.length) setAccessMessage("No invoices with PDF attachments found");
        else if (!data.invoices?.length) setAccessMessage("No invoices found. Please sync your Xero account.");
      } else {
        setInvoices([]);
        setAccessMessage(data.message || "Failed to load invoices");
      }
    } catch {
      setInvoices([]);
      setAccessMessage("Error loading invoices. Please try again.");
    } finally { setIsLoading(false); }
  }, [API_URL]);

  // ── Xero date-range handlers ──
  const applyRange = (from, to) => {
    const err = validateRange(from, to);
    setRangeError(err);
    if (err) return false;
    const range = { from, to };
    appliedRangeRef.current = range;
    setAppliedRange(range);
    setXeroSyncMessage(""); setXeroSyncError("");
    fetchInvoices(range);
    return true;
  };

  const handleApplyRange = () => applyRange(rangeFrom, rangeTo);

  const handlePresetRange = (preset) => {
    const from = preset.getFrom();
    const to   = getTodayValue();
    setRangeFrom(from); setRangeTo(to);
    applyRange(from, to);
  };

  const handleClearRange = () => {
    setRangeFrom(""); setRangeTo(""); setRangeError("");
    setXeroSyncMessage(""); setXeroSyncError("");
    appliedRangeRef.current = null;
    setAppliedRange(null);
    fetchInvoices(null);
  };

  // Pulls invoices dated inside the selected range from Xero, then shows them.
  const handleXeroRangeSync = async () => {
    const err = validateRange(rangeFrom, rangeTo);
    setRangeError(err);
    if (err) return;
    const range = { from: rangeFrom, to: rangeTo };
    setIsSyncingXero(true); setXeroSyncMessage(""); setXeroSyncError("");
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/xero/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(range),
      });
      const data  = await res.json();
      if (data.success) {
        setXeroSyncMessage(`Synced ${data.count} invoice${data.count !== 1 ? "s" : ""} dated ${formatDate(range.from)} – ${formatDate(range.to)}.`);
        appliedRangeRef.current = range;
        setAppliedRange(range);
        await fetchInvoices(range);
      } else {
        setXeroSyncError(data.message || "Failed to sync Xero invoices.");
      }
    } catch {
      setXeroSyncError("Failed to sync Xero invoices. Please try again.");
    } finally {
      setIsSyncingXero(false);
    }
  };

  // Xero is connected from the account settings page (the same place the
  // existing Xero links on this page go), rather than via /api/xero/connect.
  const handleXeroConnect = () => navigate("/setting-account2");

  // Per-tab wiring for the date-range bar.
  const RANGE_TABS = {
    xero:            { label: "Xero",             connected: xeroConnected,  syncing: isSyncingXero },
    quickbooks:      { label: "QuickBooks",       connected: qbConnected,    syncing: isSyncingQB },
    sage:            { label: "Sage",             connected: sageConnected,  syncing: isSyncingSage },
    businesscentral: { label: "Business Central", connected: bcConnected,    syncing: isSyncingBC },
    keap:            { label: "Keap",             connected: keapConnected,  syncing: isSyncingKeap },
  };
  const rangeTab = RANGE_TABS[activeTab] || null;

  // Pulls invoices dated inside the selected range from whichever accounting
  // package the active tab belongs to.
  const handleRangeSync = async () => {
    if (activeTab === "xero") return handleXeroRangeSync();
    const err = validateRange(rangeFrom, rangeTo);
    setRangeError(err);
    if (err) return;
    const range = { from: rangeFrom, to: rangeTo };
    appliedRangeRef.current = range;
    setAppliedRange(range);
    if (activeTab === "quickbooks")      await handleQBSync(range);
    else if (activeTab === "sage")       await handleSageSync(range);
    else if (activeTab === "businesscentral") await handleBCSync(range);
    else if (activeTab === "keap")       await handleKeapSync(range);
  };

  const handleQBSync = async (range) => {
    setIsSyncingQB(true); setQbSyncMessage(""); setQbSyncError("");
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/quickbooks/sync`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: syncBody(range) });
      const data  = await res.json();
      if (data.success) {
        setQbSyncMessage(`Synced ${data.count} invoice${data.count !== 1 ? "s" : ""} successfully.`);
        const invRes  = await fetch(`${API_URL}/api/quickbooks/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        const invData = await invRes.json();
        if (invData.success) {
          setQbInvoices((invData.invoices || []).map(inv => ({ ...inv, _isQuickBooks: true })));
        }
      } else if (data.requiresReauth) {
        setQbConnected(false); setQbInvoices([]);
        setQbSyncError("QuickBooks authorisation has expired. Please reconnect.");
      } else {
        setQbSyncError(data.message || "Sync failed.");
      }
    } catch (e) {
      setQbSyncError(e.message || "Sync failed.");
    } finally { setIsSyncingQB(false); }
  };

  const handleQBConnect = () => startProviderConnect("quickbooks", "quickbooks", "QuickBooks");

  const handleBCSync = async (range) => {
    setIsSyncingBC(true); setBcSyncMessage(""); setBcSyncError("");
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/business-central/sync`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: syncBody(range) });
      const data  = await res.json();
      if (data.success) {
        setBcSyncMessage(`Synced ${data.count} invoice${data.count !== 1 ? "s" : ""} successfully.`);
        const invRes  = await fetch(`${API_URL}/api/business-central/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        const invData = await invRes.json();
        if (invData.success) {
          setBcInvoices((invData.invoices || []).map(inv => ({ ...inv, _isBusinessCentral: true })));
        }
      } else if (data.requiresReauth) {
        setBcConnected(false); setBcInvoices([]);
        setBcSyncError("Business Central authorisation has expired. Please reconnect.");
      } else {
        setBcSyncError(data.message || "Sync failed.");
      }
    } catch (e) {
      setBcSyncError(e.message || "Sync failed.");
    } finally { setIsSyncingBC(false); }
  };

  const handleBCConnect = () => startProviderConnect("businesscentral", "business-central", "Business Central");

  const handleKeapSync = async (range) => {
    setIsSyncingKeap(true); setKeapSyncMessage(""); setKeapSyncError("");
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/keap/sync`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: syncBody(range) });
      const data  = await res.json();
      if (data.success) {
        setKeapSyncMessage(`Synced ${data.count} invoice${data.count !== 1 ? "s" : ""} successfully.`);
        const invRes  = await fetch(`${API_URL}/api/keap/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        const invData = await invRes.json();
        if (invData.success) {
          setKeapInvoices((invData.invoices || []).map(inv => ({ ...inv, _isKeap: true })));
        }
      } else if (data.requiresReauth) {
        setKeapConnected(false); setKeapInvoices([]);
        setKeapSyncError("Keap authorisation has expired. Please reconnect.");
      } else {
        setKeapSyncError(data.message || "Sync failed.");
      }
    } catch (e) {
      setKeapSyncError(e.message || "Sync failed.");
    } finally { setIsSyncingKeap(false); }
  };

  const handleKeapConnect = () => startProviderConnect("keap", "keap", "Keap");

  const getInvoicePdfUrl = async (invoiceId) => {
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/xero/invoice/${invoiceId}/pdf-url`, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      return data.success ? data.url : null;
    } catch { return null; }
  };

  const getUploadedInvoicePdfUrl = async (invoiceId) => {
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/documents/invoices/uploaded/${invoiceId}/pdf-url`, { headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      return data.success ? data.url : null;
    } catch { return null; }
  };

  // ── FIXED: use getInvoiceId() helper everywhere ──
  const resolvePdfUrl = async (invoice) => {
    const invoiceId = getInvoiceId(invoice);
    if (invoice._isUploaded)   return getUploadedInvoicePdfUrl(invoiceId);
    if (invoice._isQuickBooks) return getInvoicePdfUrl(invoiceId);
    if (invoice._isSage)       return getInvoicePdfUrl(invoiceId);
    if (invoice._isBusinessCentral) return getInvoicePdfUrl(invoiceId);
    if (invoice._isKeap)       return getInvoicePdfUrl(invoiceId);
    return getInvoicePdfUrl(invoiceId);
  };

  const handlePreview = async (invoice) => {
    setSelectedInvoice(invoice); setIsLoadingPreview(true); setPreviewUrl(null);
    const url = await resolvePdfUrl(invoice);
    setPreviewUrl(url); setIsLoadingPreview(false);
  };

  const handleDownload = async (invoice) => {
    const url = await resolvePdfUrl(invoice);
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.xeroInvoiceNumber || invoice.fileName || "invoice"}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } else alert("Failed to download invoice.");
  };

  const handleDownloadAll = useCallback(async () => {
    if (!activeList.length) { alert("No invoices found to download"); return; }
    setIsDownloadingAll(true);
    try {
      const zip = new JSZip(); const folder = zip.folder("Invoices");
      await Promise.all(activeList.map(async (inv) => {
        try {
          const url = await resolvePdfUrl(inv);
          if (!url) return;
          const res = await fetch(url); if (!res.ok) return;
          const invoiceId = getInvoiceId(inv);
          folder.file(`${inv.xeroInvoiceNumber || inv.fileName || invoiceId}.pdf`, await res.blob());
        } catch {}
      }));
      if (!Object.keys(folder.files).length) { alert("No PDF files available."); return; }
      saveAs(await zip.generateAsync({ type: "blob" }), `Invoices_${new Date().toISOString().slice(0, 10)}.zip`);
    } catch { alert("Failed to download invoices."); }
    finally { setIsDownloadingAll(false); }
  }, [activeList]);

  const handleDeleteUploadedInvoice = async (invoice) => {
    if (!window.confirm(`Delete "${invoice.xeroInvoiceNumber || invoice.fileName}"? This cannot be undone.`)) return;
    try {
      const invoiceId = getInvoiceId(invoice);
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/documents/invoices/uploaded/${invoiceId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      if (data.success) setUploadedInvoices(prev => prev.filter(i => getInvoiceId(i) !== invoiceId));
      else alert(data.message || "Failed to delete invoice.");
    } catch { alert("Failed to delete invoice."); }
  };

  const proceedToLBAForm = (invoice) => {
    const address   = invoice.xeroContactAddress || {};
    const invoiceId = getInvoiceId(invoice);
    navigate("/lbaform", { state: {
      id: user?.id,
     // autoIncrementAccount: user?.autoIncrementAccount,
      businessName:  invoice.xeroContactName    || "",
      invoiceNumber: invoice.xeroInvoiceNumber  || "",
      invoiceId,
      amount:        invoice.amountDue || invoice.totalAmounts || "",
      currency:      invoice.currency  || "GBP",
      dueDate:       invoice.dueDate   ? new Date(invoice.dueDate) : null,
      email:         invoice.xeroContactEmail   || "",
      houseName:     address.house  || address.line1 || "",
      street:        address.street || address.line2 || "",
      city:          address.city   || "",
      country:       address.country || "United Kingdom",
      postcode:      address.postalCode || "",
      source:        invoice._isUploaded ? "uploaded" : invoice._isQuickBooks ? "quickbooks" : invoice._isSage ? "sage" : invoice._isBusinessCentral ? "businesscentral" : invoice._isKeap ? "keap" : "xero",
      xeroInvoiceData: invoice,
    }});
  };

  // ── FIXED: gate on either provider, not just Stripe ──
  const handleSendToLBA = (invoice) => {
    if (!isAnyProviderReady) {
      setPendingInvoice(invoice); setUpdatePaymentOpen(true); return;
    }
    proceedToLBAForm(invoice);
  };

  const handleSageSync = async (range) => {
    setIsSyncingSage(true); setSageSyncMessage(""); setSageSyncError("");
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/sage/sync`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: syncBody(range) });
      const data  = await res.json();
      if (data.success) {
        setSageSyncMessage(`Synced ${data.count} invoice${data.count !== 1 ? "s" : ""} successfully.`);
        const invRes  = await fetch(`${API_URL}/api/sage/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        const invData = await invRes.json();
        if (invData.success) setSageInvoices((invData.invoices || []).map(inv => ({ ...inv, _isSage: true })));
      } else if (data.requiresReauth) {
        setSageConnected(false); setSageInvoices([]);
        setSageSyncError("Sage authorisation has expired. Please reconnect.");
      } else {
        setSageSyncError(data.message || "Sync failed.");
      }
    } catch (e) {
      setSageSyncError(e.message || "Sync failed.");
    } finally { setIsSyncingSage(false); }
  };

  const handleSageConnect = () => startProviderConnect("sage", "sage", "Sage");

  const closeUpdatePayment = useCallback(() => { setUpdatePaymentOpen(false); setPendingInvoice(null); }, []);

  // Opens the payment provider modal on its own — not gated on isAnyProviderReady,
  // since "Send to LBA" only opens that modal when NO provider is connected yet.
  // Without this, a connected Paystack account has no way back into the modal
  // (and therefore no way to reach the Disconnect option) once it's active.
  const openPaymentSettings = useCallback(() => {
    setPendingInvoice(null);
    setUpdatePaymentOpen(true);
  }, []);

  const handleStripeConnect = async () => {
  try {
    if (!stripeAccountStatus?.connected) {
      const res = await stripeService.createConnectAccount();
      if (res?.onboardingUrl) {
        window.location.href = res.onboardingUrl;
      } else if (res?.accountId) {
        // Account already existed server-side but had no link attached —
        // fall back to the resume-onboarding endpoint instead of dead-ending.
        window.location.href = (await stripeService.getOnboardingLink()).onboardingUrl;
      } else {
        alert(res?.message || "Failed to connect to Stripe.");
      }
    } else if (!stripeAccountStatus?.onboardingComplete) {
      window.location.href = (await stripeService.getOnboardingLink()).onboardingUrl;
    } else {
      if (pendingInvoice) { proceedToLBAForm(pendingInvoice); closeUpdatePayment(); }
      else window.open((await stripeService.getDashboardLink()).dashboardUrl, "_blank");
    }
  } catch (e) {
    alert("Failed to connect to Stripe.");
  }
};

  // ── Refresh Paystack status only — no side effects. Used by both the
  // connect and disconnect flows so status is always re-fetched from the
  // server rather than guessed at client-side. ──
  const refreshPaystackStatus = async () => {
    const data = await paystackService.getAccountStatus();
    setPaystackAccountStatus(data);
    return data;
  };

  // ── Called after a successful Paystack connect: refresh status AND
  // continue with the invoice that was already picked (mirrors Stripe's
  // "already connected" continuation). ──
  const handlePaystackConnected = async () => {
    try {
      await refreshPaystackStatus();
      if (pendingInvoice) {
        proceedToLBAForm(pendingInvoice);
        closeUpdatePayment();
      }
    } catch {
      // Status refresh failed — leave the modal open so the user can retry rather than
      // silently proceeding with a possibly-stale connection state.
    }
  };

  // ── FIXED: disconnect no longer reuses handlePaystackConnected, which
  // would incorrectly navigate to /lbaform via pendingInvoice — there is no
  // active provider immediately after a disconnect, so nothing should proceed.
  // This just performs the disconnect and refreshes status; UpdatePayment's
  // own effect (watching paystackAccountStatus.connected) handles falling
  // back to the connect form in the UI. ──
  const handlePaystackDisconnect = async () => {
    const data = await paystackService.disconnectAccount();
    await refreshPaystackStatus();
    return data;
  };
  const refreshStripeStatus = async () => {
  const data = await stripeService.getAccountStatus();
  setStripeAccountStatus(data);
  return data;
};

const handleStripeDisconnect = async () => {
  const data = await stripeService.disconnectAccount();
  await refreshStripeStatus();
  return data;
};

  const closePreview = () => { setSelectedInvoice(null); setPreviewUrl(null); setIsLoadingPreview(false); };

  const checkXeroStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res   = await fetch(`${API_URL}/api/xero/status`, { headers: { Authorization: `Bearer ${token}` } });
      const data  = await res.json();
      setXeroConnected(!!(data.success && data.connected));
      if (data.success && !data.connected) setAccessMessage(data.message || "Xero is not connected");
    } catch {}
    finally { setXeroStatusChecked(true); }
  }, [API_URL]);

  const handleSendEmail = async ({ invoice, extraCharge, extraDescription, editedEmail, emailSubject, emailBody, logoDataUrl, paymentDetails }) => {
    const token      = getAuthToken();
    const invoiceId  = getInvoiceId(invoice);
    const total      = (parseFloat(invoice.amountDue || invoice.totalAmounts || 0)) + (parseFloat(extraCharge) || 0);
    const extraAmt   = parseFloat(extraCharge) || 0;
    const baseAmount = parseFloat(invoice.amountDue)
      || parseFloat(String(invoice.totalAmounts || "0").replace(/,/g, ""))
      || 0;

    const { jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const margin = 20;
    const pageWidth = 210;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = margin;

    const primaryColor = [15, 23, 42];
    const grayColor    = [71, 85, 105];
    const lightGray    = [241, 245, 249];
    const borderColor  = [226, 232, 240];
    const redColor     = [153, 27, 27];

    let logoBottomY = yPos + 14;

    if (logoDataUrl) {
      try {
        const img = new Image();
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = logoDataUrl; });
        const maxW = 55, maxH = 22;
        const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
        const drawW = img.naturalWidth * ratio, drawH = img.naturalHeight * ratio;
        const ext = logoDataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
        pdf.addImage(logoDataUrl, ext, margin, yPos, drawW, drawH, undefined, "FAST");
        logoBottomY = yPos + drawH;
      } catch {
        pdf.setFontSize(18); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...primaryColor);
        pdf.text("Invoice", margin, yPos + 12); logoBottomY = yPos + 14;
      }
    } else {
      pdf.setFontSize(18); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...primaryColor);
      pdf.text("Invoice", margin, yPos + 12); logoBottomY = yPos + 14;
    }

    const addrLines = [paymentDetails.companyName, ...(paymentDetails.address ? paymentDetails.address.split("\n") : [])].filter(Boolean);
    let addrBottomY = yPos;
    addrLines.forEach((line, i) => {
      const lineY = yPos + i * 5;
      if (i === 0) { pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...primaryColor); }
      else { pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...grayColor); }
      pdf.text(line, pageWidth - margin, lineY + 4, { align: "right" });
      addrBottomY = lineY + 6;
    });

    yPos = Math.max(logoBottomY, addrBottomY) + 6;
    pdf.setDrawColor(...borderColor); pdf.setLineWidth(0.2);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    const titleY = yPos;
    pdf.setFontSize(20); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...primaryColor);
    pdf.text("TAX INVOICE", margin, titleY);

    const metaX = pageWidth - margin - 70;
    const metaData = [
      ["INVOICE DATE",   new Date(invoice.invoiceDates || new Date()).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })],
      ["CURRENCY",       invoice.currency || "GBP"],
      ["INVOICE NUMBER", invoice.xeroInvoiceNumber || invoice.invoiceNumbers || "—"],
      ["STATUS",         invoice.status || "—"],
    ];

    let metaY = titleY - 4;
    metaData.forEach(([label, value]) => {
      pdf.setFontSize(7); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...grayColor);
      pdf.text(label, metaX, metaY);
      pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...primaryColor);
      pdf.text(String(value), metaX, metaY + 4);
      metaY += 10;
    });

    const metaBottomY = metaY + 2;
    const xeroAddress = invoice.xeroContactAddress || {};
    const billToLines = [xeroAddress.house || xeroAddress.line1, xeroAddress.street || xeroAddress.line2, xeroAddress.city, xeroAddress.region, xeroAddress.postalCode, xeroAddress.country].filter(Boolean);

    let billY = titleY + 8;
    if (invoice.xeroContactName) {
      pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...primaryColor);
      pdf.text(invoice.xeroContactName, margin, billY); billY += 5;
    }
    pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...grayColor);
    billToLines.forEach((line) => { pdf.text(line, margin, billY); billY += 4.5; });
    const recipientEmail = editedEmail || invoice.xeroContactEmail || "";
    if (recipientEmail) { billY += 2; pdf.setFontSize(8.5); pdf.text(recipientEmail, margin, billY); billY += 5; }

    yPos = Math.max(billY, metaBottomY) + 8;

    const tableRows = [[invoice.xeroDescription || `Invoice — ${invoice.xeroInvoiceNumber || ""}`, "1.00", baseAmount.toFixed(2)]];
    if (extraAmt > 0) tableRows.push([extraDescription || "Additional charge", "1.00", extraAmt.toFixed(2)]);

    autoTable(pdf, {
      startY: yPos,
      head: [["Description", "Quantity", `Amount ${invoice.currency || "GBP"}`]],
      body: tableRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3, textColor: primaryColor, lineColor: borderColor, lineWidth: 0.1 },
      headStyles: { fillColor: lightGray, textColor: primaryColor, fontStyle: "bold", lineColor: borderColor, lineWidth: 0.3 },
      columnStyles: { 0: { cellWidth: contentWidth * 0.6 }, 1: { cellWidth: contentWidth * 0.15, halign: "right" }, 2: { cellWidth: contentWidth * 0.25, halign: "right" } },
      theme: "grid",
    });

    yPos = pdf.lastAutoTable.finalY + 4;
    const totalsX = margin + contentWidth * 0.6, totalsW = contentWidth * 0.4;
    const totalsRows = [["Subtotal", baseAmount.toFixed(2)]];
    if (extraAmt > 0) totalsRows.push([extraDescription || "Extra charge", extraAmt.toFixed(2)]);
    totalsRows.push(["TOTAL " + (invoice.currency || "GBP"), total.toFixed(2)]);

    totalsRows.forEach((row, i) => {
      const isTotal = i === totalsRows.length - 1;
      const rowY = yPos + i * 7;
      if (isTotal) { pdf.setDrawColor(...borderColor); pdf.setLineWidth(0.3); pdf.line(totalsX, rowY - 2, totalsX + totalsW, rowY - 2); }
      pdf.setFontSize(isTotal ? 10 : 9); pdf.setFont("helvetica", isTotal ? "bold" : "normal"); pdf.setTextColor(...primaryColor);
      pdf.text(row[0], totalsX + 2, rowY + 4);
      pdf.text(row[1], totalsX + totalsW - 2, rowY + 4, { align: "right" });
    });

    yPos += totalsRows.length * 7 + 10;
    const isInvoiceOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date();
    const dueDateStr = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

    pdf.setDrawColor(...borderColor); pdf.setLineWidth(0.2);
    pdf.line(margin, yPos, margin + contentWidth, yPos);
    yPos += 6;
    pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.setTextColor(isInvoiceOverdue ? redColor[0] : primaryColor[0], isInvoiceOverdue ? redColor[1] : primaryColor[1], isInvoiceOverdue ? redColor[2] : primaryColor[2]);
    pdf.text(`Due Date: ${dueDateStr}${isInvoiceOverdue ? "  [OVERDUE]" : ""}`, margin, yPos);
    yPos += 7;
    pdf.setFontSize(8); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...grayColor);
    pdf.text("We reserve the right to charge interest on overdue amounts at 8% above the Bank of England base rate.", margin, yPos, { maxWidth: contentWidth });
    yPos += 10;

    if (paymentDetails?.companyName || paymentDetails?.accountNo || paymentDetails?.sortCode || paymentDetails?.payUrl) {
      pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...primaryColor);
      pdf.text("Please make payments to:", margin, yPos); yPos += 5;
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...grayColor);
      if (paymentDetails.companyName) { pdf.text(paymentDetails.companyName, margin, yPos); yPos += 5; }
      if (paymentDetails.accountNo)   { pdf.text(`Account No - ${paymentDetails.accountNo}`, margin, yPos); yPos += 5; }
      if (paymentDetails.sortCode)    { pdf.text(`Sort Code - ${paymentDetails.sortCode}`, margin, yPos); yPos += 5; }
      if (paymentDetails.payUrl)      { pdf.setTextColor(37, 99, 235); pdf.text(paymentDetails.payUrl, margin, yPos); yPos += 5; }
    }

    const pdfBlob   = pdf.output("blob");
    const pdfBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    const res = await fetch(`${API_URL}/api/xero/send-invoice`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        to:            editedEmail,
        subject:       emailSubject,
        body:          emailBody,
        invoiceNumber: invoice.xeroInvoiceNumber,
        invoiceId,                              // ← fixed: uses getInvoiceId()
        extraCharge:   parseFloat(extraCharge) || 0,
        extraDescription,
        totalAmount:   total,
        currency:      invoice.currency || "GBP",
        paymentDetails: paymentDetails || {},
        pdfBase64,
        isUploaded:    invoice._isUploaded    || false,
        isQuickBooks:  invoice._isQuickBooks  || false,
        isSage:        invoice._isSage        || false,
        isBusinessCentral: invoice._isBusinessCentral || false,
        isKeap:        invoice._isKeap        || false,
      }),
    });

    const data = await res.json();
    if (!data.success) throw new Error(data.message || "Failed to send email");
  };

  const totalPages      = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex      = (currentPage - 1) * itemsPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);
  const handlePageChange = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); };

  useEffect(() => { setCurrentPage(1); }, [activeList.length]);
  useEffect(() => { checkXeroStatus(); fetchInvoices(); }, [fetchInvoices, checkXeroStatus]);

  const hasDefaults = !!(defaultLogo || defaultPaymentDetails?.companyName || defaultPaymentDetails?.address || defaultPaymentDetails?.accountNo || defaultPaymentDetails?.sortCode || defaultPaymentDetails?.payUrl);

  const ConnectErrorLogo = connectError ? PROVIDER_LOGOS[connectError.provider] : null;

  // Xero is known to be disconnected (status call has settled).
  const xeroNotConnected = xeroStatusChecked && !xeroConnected;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');
        html, body { background: rgb(241,245,249) !important; }
        .dd-root * { box-sizing: border-box; }
        .dd-root { font-family: 'DM Sans', sans-serif; background: rgb(241,245,249) !important; min-height: 100vh; }
        .dd-card { background: #ffffff; border: 1px solid #E2E8F0; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04); overflow: hidden; transition: box-shadow 0.2s ease, transform 0.2s ease; }
        .dd-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.09); transform: translateY(-2px); }
        .dd-card.uploaded-card { border-color: #EDE9FE; }
        .dd-card.uploaded-card:hover { box-shadow: 0 4px 24px rgba(124,58,237,0.12); }
        .dd-card.qb-card { border-color: #DCFCE7; }
        .dd-card.qb-card:hover { box-shadow: 0 4px 24px rgba(22,163,74,0.12); }
        .dd-card.sage-card { border-color: #BBF7D0; }
        .dd-card.sage-card:hover { box-shadow: 0 4px 24px rgba(0,180,0,0.12); }
        .dd-card.bc-card { border-color: #BFDBFE; }
        .dd-card.bc-card:hover { box-shadow: 0 4px 24px rgba(15,108,189,0.14); }
        .dd-card.keap-card { border-color: #FED7AA; }
        .dd-card.keap-card:hover { box-shadow: 0 4px 24px rgba(234,88,12,0.14); }
        .xero-card-header { padding: 16px 20px; border-bottom: 1px solid #F1F5F9; display: flex; align-items: flex-start; justify-content: space-between; background: #FAFBFD; }
        .xero-card-header.uploaded-header { background: #FDFBFF; }
        .xero-card-header.qb-header { background: #F7FEF9; }
        .xero-card-header.sage-header { background: #F0FDF4; }
        .xero-card-header.bc-header { background: #F5F9FF; }
        .xero-card-header.keap-header { background: #FFF7ED; }
        .xero-invoice-number { font-size: 14px; font-weight: 700; color: #0F172A; font-family: 'DM Mono', monospace; }
        .xero-contact-name { font-size: 12px; color: #64748B; margin-top: 2px; }
        .xero-status-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
        .dd-card-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 10px; }
        .xero-detail-row { display: flex; align-items: center; justify-content: space-between; }
        .xero-detail-label { font-size: 12px; color: #94A3B8; }
        .xero-detail-value { font-size: 13px; color: #1E293B; font-weight: 500; }
        .xero-detail-value.mono { font-family: 'DM Mono', monospace; }
        .xero-description-row { padding: 8px 10px; background: #F8FAFC; border: 1px solid #F1F5F9; border-radius: 8px; font-size: 12px; color: #475569; line-height: 1.5; font-style: italic; }
        .dd-card-footer { padding: 12px 20px; border-top: 1px solid #F1F5F9; display: flex; align-items: center; gap: 8px; background: #FAFBFD; flex-wrap: wrap; }
        .xero-search { display: flex; align-items: center; gap: 8px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px; padding: 9px 14px; width: 280px; transition: border-color 0.15s, box-shadow 0.15s; }
        .xero-range-bar { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; padding: 14px 16px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 12px; }
        .xero-range-field { display: flex; flex-direction: column; gap: 4px; }
        .xero-range-field label { font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; }
        .xero-range-field input { padding: 8px 10px; border: 1px solid #E2E8F0; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; color: #0F172A; outline: none; background: #fff; }
        .xero-range-field input:focus { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .xero-range-presets { display: flex; gap: 6px; flex-wrap: wrap; }
        .xero-range-preset { padding: 8px 12px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; color: #475569; cursor: pointer; transition: all 0.15s; }
        .xero-range-preset:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .xero-range-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid #E2E8F0; background: #ffffff; color: #1E293B; transition: all 0.15s; }
        .xero-range-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .xero-range-btn.primary { background: #2563EB; border-color: #2563EB; color: #ffffff; }
        .xero-range-btn.primary:hover { background: #1D4ED8; color: #ffffff; }
        .xero-range-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .xero-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .xero-search input { border: none; outline: none; background: transparent; font-family: 'DM Sans', sans-serif; font-size: 13px; color: #0F172A; width: 100%; }
        .xero-search input::placeholder { color: #94A3B8; }
        .xero-page-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A; cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s; }
        .xero-page-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .xero-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .xero-dl-all-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B; cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s; }
        .xero-dl-all-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .xero-dl-all-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .xero-settings-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B; cursor: pointer; transition: all 0.15s; position: relative; }
        .xero-settings-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .xero-settings-dot { position: absolute; top: -3px; right: -3px; width: 9px; height: 9px; border-radius: 50%; background: #10B981; border: 2px solid #fff; }
        .xero-upload-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #7C3AED; border: 1px solid #7C3AED; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .xero-upload-btn:hover { background: #6D28D9; border-color: #6D28D9; }
        .xero-sync-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #13B5EA; border: 1px solid #13B5EA; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .xero-sync-btn:hover:not(:disabled) { background: #0E9AC9; border-color: #0E9AC9; }
        .xero-sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .xero-connect-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 22px; background: #13B5EA; border: 1px solid #13B5EA; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .xero-connect-btn:hover:not(:disabled) { background: #0E9AC9; border-color: #0E9AC9; }
        .xero-connect-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .qb-sync-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #16A34A; border: 1px solid #16A34A; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .qb-sync-btn:hover:not(:disabled) { background: #15803D; border-color: #15803D; }
        .qb-sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .qb-connect-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 22px; background: #16A34A; border: 1px solid #16A34A; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .qb-connect-btn:hover:not(:disabled) { background: #15803D; border-color: #15803D; }
        .qb-connect-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .sage-sync-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #00DC06; border: 1px solid #00DC06; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .sage-sync-btn:hover:not(:disabled) { background: #00b805; border-color: #00b805; }
        .sage-sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .sage-connect-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 22px; background: #00DC06; border: 1px solid #00DC06; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .sage-connect-btn:hover:not(:disabled) { background: #00b805; border-color: #00b805; }
        .sage-connect-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .bc-sync-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #0F6CBD; border: 1px solid #0F6CBD; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .bc-sync-btn:hover:not(:disabled) { background: #0C5AA0; border-color: #0C5AA0; }
        .bc-sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .bc-connect-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 22px; background: #0F6CBD; border: 1px solid #0F6CBD; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .bc-connect-btn:hover:not(:disabled) { background: #0C5AA0; border-color: #0C5AA0; }
        .bc-connect-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .keap-sync-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; background: #EA580C; border: 1px solid #EA580C; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .keap-sync-btn:hover:not(:disabled) { background: #C2410C; border-color: #C2410C; }
        .keap-sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .keap-connect-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 22px; background: #EA580C; border: 1px solid #EA580C; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer; transition: all 0.15s; }
        .keap-connect-btn:hover:not(:disabled) { background: #C2410C; border-color: #C2410C; }
        .keap-connect-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .xero-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; text-align: center; gap: 12px; }
        .xero-empty-icon { width: 56px; height: 56px; border-radius: 16px; background: #F1F5F9; display: flex; align-items: center; justify-content: center; margin-bottom: 4px; }
        .xero-modal { width: 90vw; height: 90vh; background: #fff; border-radius: 16px; display: flex; flex-direction: column; border: 1px solid #E2E8F0; box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden; }
        .xero-modal-header { padding: 18px 24px; border-bottom: 1px solid #E2E8F0; display: flex; align-items: center; justify-content: space-between; background: #FAFBFD; }
        .xero-icon-btn { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; background: #ffffff; border: 1px solid #E2E8F0; cursor: pointer; transition: border-color 0.15s, background 0.15s; flex-shrink: 0; }
        .xero-icon-btn:hover { border-color: #2563EB; background: #EFF6FF; }
        .xero-icon-btn.danger:hover { border-color: #FECACA; background: #FEF2F2; }
        .xero-email-btn { display: inline-flex; align-items: center; gap: 5px; padding: 7px 13px; background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; color: #1E40AF; cursor: pointer; transition: all 0.15s; flex-shrink: 0; }
        .xero-email-btn:hover { background: #DBEAFE; border-color: #93C5FD; }
        .xero-overdue-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #FEF2F2; border: 1px solid #FECACA; border-radius: 6px; font-size: 10px; font-weight: 700; color: #991B1B; margin-left: 6px; }
        .xero-uploaded-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #F5F3FF; border: 1px solid #DDD6FE; border-radius: 6px; font-size: 10px; font-weight: 700; color: #6D28D9; margin-left: 6px; }
        .xero-qb-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #DCFCE7; border: 1px solid #86EFAC; border-radius: 6px; font-size: 10px; font-weight: 700; color: #15803D; margin-left: 6px; }
        .xero-sage-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #DCFCE7; border: 1px solid #86EFAC; border-radius: 6px; font-size: 10px; font-weight: 700; color: #15803D; margin-left: 6px; }
        .xero-bc-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #DBEAFE; border: 1px solid #93C5FD; border-radius: 6px; font-size: 10px; font-weight: 700; color: #1E40AF; margin-left: 6px; }
        .xero-keap-tag { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; background: #FFEDD5; border: 1px solid #FED7AA; border-radius: 6px; font-size: 10px; font-weight: 700; color: #C2410C; margin-left: 6px; }
        .tab-bar { display: flex; align-items: center; gap: 4px; background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 4px; }
        .tab-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; border-radius: 9px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; border: none; background: transparent; color: #64748B; transition: all 0.15s; }
        .tab-btn.active { background: #F1F5F9; color: #0F172A; }
        .tab-btn:hover:not(.active) { background: #F8FAFC; color: #1E293B; }
        .tab-count { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px; font-size: 11px; font-weight: 700; background: #E2E8F0; color: #475569; }
        .tab-btn.active .tab-count { background: #fff; }
        .tab-btn.active.tab-xero .tab-count { background: #E0F5FD; color: #0B7FA8; }
        .tab-btn.active.tab-uploaded .tab-count { background: #EDE9FE; color: #7C3AED; }
        .tab-btn.active.tab-qb .tab-count { background: #DCFCE7; color: #15803D; }
        .tab-btn.active.tab-sage .tab-count { background: #DCFCE7; color: #15803D; }
        .tab-btn.active.tab-bc .tab-count { background: #DBEAFE; color: #1E40AF; }
        .tab-btn.active.tab-keap .tab-count { background: #FFEDD5; color: #C2410C; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner { width: 20px; height: 20px; border: 2px solid #E2E8F0; border-top-color: #2563EB; border-radius: 50%; animation: spin 0.7s linear infinite; }
        .spinner-green { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block; }

        /* ── Connect-error toast ── */
        @keyframes connect-toast-in { from { opacity: 0; transform: translateY(-10px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes connect-toast-timer { from { transform: scaleX(1); } to { transform: scaleX(0); } }
        .connect-toast { position: fixed; top: 24px; right: 24px; z-index: 1200; width: 380px; max-width: calc(100vw - 48px); background: #ffffff; border: 1px solid #FECACA; border-radius: 14px; box-shadow: 0 12px 40px rgba(15,23,42,0.16), 0 2px 8px rgba(15,23,42,0.06); overflow: hidden; font-family: 'DM Sans', sans-serif; animation: connect-toast-in 0.22s ease-out; }
        .connect-toast-inner { display: flex; align-items: flex-start; gap: 12px; padding: 14px 14px 14px 16px; }
        .connect-toast-icon { position: relative; width: 38px; height: 38px; border-radius: 10px; background: #FEF2F2; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .connect-toast-icon-badge { position: absolute; right: -5px; bottom: -5px; width: 18px; height: 18px; border-radius: 50%; background: #DC2626; border: 2px solid #fff; display: flex; align-items: center; justify-content: center; }
        .connect-toast-title { font-size: 14px; font-weight: 700; color: #0F172A; line-height: 1.3; }
        .connect-toast-message { font-size: 13px; color: #475569; line-height: 1.5; margin-top: 3px; word-break: break-word; }
        .connect-toast-close { width: 26px; height: 26px; border-radius: 7px; border: 1px solid transparent; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: background 0.15s, border-color 0.15s; }
        .connect-toast-close:hover { background: #F1F5F9; border-color: #E2E8F0; }
        .connect-toast-timer { height: 3px; background: #DC2626; transform-origin: left; animation: connect-toast-timer 8s linear forwards; }
        @media (prefers-reduced-motion: reduce) { .connect-toast { animation: none; } }
      `}</style>

      {/* ── Connect-error toast (backend message from a provider /connect call) ── */}
      {connectError && (
        <div className="connect-toast" role="alert" aria-live="assertive">
          <div className="connect-toast-inner">
            <div className="connect-toast-icon">
              {ConnectErrorLogo ? <ConnectErrorLogo size={22} /> : <AlertCircle size={20} style={{ color: "#DC2626" }} />}
              {ConnectErrorLogo && (
                <span className="connect-toast-icon-badge">
                  <X size={10} strokeWidth={3} style={{ color: "#fff" }} />
                </span>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="connect-toast-title">Couldn't connect {connectError.label}</div>
              <div className="connect-toast-message">{connectError.message}</div>
            </div>
            <button className="connect-toast-close" onClick={() => setConnectError(null)} aria-label="Dismiss">
              <X size={14} style={{ color: "#64748B" }} />
            </button>
          </div>
          <div className="connect-toast-timer" key={`${connectError.provider}-${connectError.message}`} />
        </div>
      )}

      <div className="dd-root w-full overflow-visible" style={{ background: "rgb(241,245,249)", minHeight: "100vh" }}>
        <NavigationPanel />

        <div style={{ position: "absolute", top: 136, width: "100%", padding: "0 96px 64px", display: "flex", flexDirection: "column", gap: 28 }}>

          {/* Page header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Invoices</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
                {isLoading ? "Loading invoices…" : `Showing ${Math.min(startIndex + 1, filteredInvoices.length)}–${Math.min(startIndex + itemsPerPage, filteredInvoices.length)} of ${filteredInvoices.length} invoice${filteredInvoices.length !== 1 ? "s" : ""}${searchQuery ? " (filtered)" : ""}`}
              </p>
              {accessMessage && activeTab === "xero" && xeroConnected && <p style={{ margin: "6px 0 0", fontSize: 13, color: "#D97706" }}>{accessMessage}</p>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div className="xero-search">
                <Search size={15} style={{ color: "#94A3B8", flexShrink: 0 }} />
                <input type="text" placeholder="Search invoices…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}><X size={14} style={{ color: "#94A3B8" }} /></button>}
              </div>
              <button className="xero-settings-btn" onClick={openPaymentSettings}>
                <Settings size={15} /> Payment settings
                {isAnyProviderReady && <span className="xero-settings-dot" />}
              </button>
              <button className="xero-settings-btn" onClick={() => setEmailSettingsOpen(true)}>
                <Settings size={15} /> Email defaults
                {hasDefaults && <span className="xero-settings-dot" />}
              </button>
             
              <button className="xero-upload-btn" onClick={() => setUploadModalOpen(true)}>
                <Upload size={15} /> Upload invoices
              </button>
              {activeTab === "quickbooks" && qbConnected && (
                <button className="qb-sync-btn" onClick={handleQBSync} disabled={isSyncingQB}>
                  {isSyncingQB ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync QB</>}
                </button>
              )}
              {activeTab === "sage" && sageConnected && (
                <button className="sage-sync-btn" onClick={handleSageSync} disabled={isSyncingSage}>
                  {isSyncingSage ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync Sage</>}
                </button>
              )}
              {activeTab === "businesscentral" && bcConnected && (
                <button className="bc-sync-btn" onClick={handleBCSync} disabled={isSyncingBC}>
                  {isSyncingBC ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync BC</>}
                </button>
              )}
              {activeTab === "keap" && keapConnected && (
                <button className="keap-sync-btn" onClick={handleKeapSync} disabled={isSyncingKeap}>
                  {isSyncingKeap ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync Keap</>}
                </button>
              )}
              <button className="xero-dl-all-btn" onClick={handleDownloadAll} disabled={isDownloadingAll || !activeList.length}>
                {isDownloadingAll ? <><span className="spinner" />Downloading…</> : <><Download size={15} />Download all</>}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div className="tab-bar">
              <button className={`tab-btn tab-xero${activeTab === "xero" ? " active" : ""}`} onClick={() => { setActiveTab("xero"); setSearchQuery(""); }}>
                <XeroLogo size={14} /> Xero
                <span className="tab-count">{invoices.length}</span>
              </button>
              <button className={`tab-btn tab-uploaded${activeTab === "uploaded" ? " active" : ""}`} onClick={() => { setActiveTab("uploaded"); setSearchQuery(""); }}>
                <Upload size={14} /> Uploaded
                <span className="tab-count">{uploadedInvoices.length}</span>
              </button>
              <button className={`tab-btn tab-qb${activeTab === "quickbooks" ? " active" : ""}`} onClick={() => { setActiveTab("quickbooks"); setSearchQuery(""); }}>
                <QBLogo size={14} /> QuickBooks
                <span className="tab-count">{qbInvoices.length}</span>
              </button>
              <button className={`tab-btn tab-sage${activeTab === "sage" ? " active" : ""}`} onClick={() => { setActiveTab("sage"); setSearchQuery(""); }}>
                <SageLogo size={14} /> Sage
                <span className="tab-count">{sageInvoices.length}</span>
              </button>
              <button className={`tab-btn tab-bc${activeTab === "businesscentral" ? " active" : ""}`} onClick={() => { setActiveTab("businesscentral"); setSearchQuery(""); }}>
                <BCLogo size={14} /> Business Central
                <span className="tab-count">{bcInvoices.length}</span>
              </button>
              <button className={`tab-btn tab-keap${activeTab === "keap" ? " active" : ""}`} onClick={() => { setActiveTab("keap"); setSearchQuery(""); }}>
                <KeapLogo size={14} /> Keap
                <span className="tab-count">{keapInvoices.length}</span>
              </button>
            </div>

            {activeTab === "xero" && xeroNotConnected && (
              <div style={{ fontSize: 13, color: "#94A3B8", display: "flex", alignItems: "center", gap: 6 }}>
                <XeroLogo size={13} /> Not connected —
                <button onClick={handleXeroConnect} style={{ background: "none", border: "none", color: "#0E9AC9", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Connect Xero</button>
              </div>
            )}
            {activeTab === "uploaded" && uploadedInvoices.length === 0 && (
              <div style={{ fontSize: 13, color: "#94A3B8" }}>No uploaded invoices yet — click "Upload invoices" to add some.</div>
            )}
            {activeTab === "quickbooks" && !qbConnected && (
              <div style={{ fontSize: 13, color: "#94A3B8", display: "flex", alignItems: "center", gap: 6 }}>
                <QBLogo size={13} /> Not connected —
                <button onClick={handleQBConnect} style={{ background: "none", border: "none", color: "#16A34A", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Connect QuickBooks</button>
              </div>
            )}
            {activeTab === "quickbooks" && qbSyncMessage && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#15803D", background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 8, padding: "5px 12px" }}>
                <Check size={13} /> {qbSyncMessage}
              </div>
            )}
            {activeTab === "quickbooks" && qbSyncError && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "5px 12px" }}>
                <AlertCircle size={13} /> {qbSyncError}
              </div>
            )}
            {activeTab === "sage" && !sageConnected && (
              <div style={{ fontSize: 13, color: "#94A3B8", display: "flex", alignItems: "center", gap: 6 }}>
                <SageLogo size={13} /> Not connected —
                <button onClick={handleSageConnect} style={{ background: "none", border: "none", color: "#00b805", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Connect Sage</button>
              </div>
            )}
            {activeTab === "sage" && sageSyncMessage && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#15803D", background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 8, padding: "5px 12px" }}>
                <Check size={13} /> {sageSyncMessage}
              </div>
            )}
            {activeTab === "sage" && sageSyncError && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "5px 12px" }}>
                <AlertCircle size={13} /> {sageSyncError}
              </div>
            )}
            {activeTab === "businesscentral" && !bcConnected && (
              <div style={{ fontSize: 13, color: "#94A3B8", display: "flex", alignItems: "center", gap: 6 }}>
                <BCLogo size={13} /> Not connected —
                <button onClick={handleBCConnect} style={{ background: "none", border: "none", color: "#0F6CBD", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Connect Business Central</button>
              </div>
            )}
            {activeTab === "businesscentral" && bcSyncMessage && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#1E40AF", background: "#DBEAFE", border: "1px solid #93C5FD", borderRadius: 8, padding: "5px 12px" }}>
                <Check size={13} /> {bcSyncMessage}
              </div>
            )}
            {activeTab === "businesscentral" && bcSyncError && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "5px 12px" }}>
                <AlertCircle size={13} /> {bcSyncError}
              </div>
            )}
            {activeTab === "keap" && !keapConnected && (
              <div style={{ fontSize: 13, color: "#94A3B8", display: "flex", alignItems: "center", gap: 6 }}>
                <KeapLogo size={13} /> Not connected —
                <button onClick={handleKeapConnect} style={{ background: "none", border: "none", color: "#EA580C", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Connect Keap</button>
              </div>
            )}
            {activeTab === "keap" && keapSyncMessage && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#C2410C", background: "#FFEDD5", border: "1px solid #FED7AA", borderRadius: 8, padding: "5px 12px" }}>
                <Check size={13} /> {keapSyncMessage}
              </div>
            )}
            {activeTab === "keap" && keapSyncError && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "5px 12px" }}>
                <AlertCircle size={13} /> {keapSyncError}
              </div>
            )}
          </div>

          {/* Invoice-date range — every accounting tab (not Uploaded) */}
          {rangeTab && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div className="xero-range-bar">
                <div className="xero-range-field">
                  <label htmlFor="xero-range-from">Invoice date from</label>
                  <input id="xero-range-from" type="date" value={rangeFrom} min={getMinRangeValue()} max={rangeTo || getTodayValue()}
                    onChange={(e) => { setRangeFrom(e.target.value); setRangeError(""); }} />
                </div>
                <div className="xero-range-field">
                  <label htmlFor="xero-range-to">To</label>
                  <input id="xero-range-to" type="date" value={rangeTo} min={rangeFrom || getMinRangeValue()} max={getTodayValue()}
                    onChange={(e) => { setRangeTo(e.target.value); setRangeError(""); }} />
                </div>
                <div className="xero-range-presets">
                  {RANGE_PRESETS.map((preset) => (
                    <button key={preset.id} className="xero-range-preset" onClick={() => handlePresetRange(preset)} disabled={rangeTab.syncing}>{preset.label}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
                  <button className="xero-range-btn" onClick={handleApplyRange} disabled={!rangeFrom || !rangeTo || rangeTab.syncing || (activeTab === "xero" && isLoading)}>Apply</button>
                  <button className="xero-range-btn primary" onClick={handleRangeSync} disabled={!rangeFrom || !rangeTo || rangeTab.syncing || !rangeTab.connected}
                    title={`Pull invoices dated in this range from ${rangeTab.label}`}>
                    {rangeTab.syncing ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={14} /> Sync from {rangeTab.label}</>}
                  </button>
                  {(appliedRange || rangeFrom || rangeTo) && (
                    <button className="xero-range-btn" onClick={handleClearRange} disabled={rangeTab.syncing}><X size={14} /> Clear</button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#94A3B8" }}>
                {appliedRange
                  ? <>Showing invoices dated <strong style={{ color: "#475569" }}>{formatDate(appliedRange.from)} – {formatDate(appliedRange.to)}</strong>.</>
                  : "Showing all synced invoices."}{" "}
                The range can start up to {MAX_RANGE_YEARS} years back ({formatDate(getMinRangeValue())}).
              </div>
              {rangeError && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, fontSize: 13, color: "#991B1B" }}>
                  <AlertCircle size={15} style={{ flexShrink: 0 }} /> {rangeError}
                </div>
              )}
              {activeTab === "xero" && xeroSyncMessage && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, fontSize: 13, color: "#166534" }}>
                  <Check size={15} style={{ flexShrink: 0 }} /> {xeroSyncMessage}
                </div>
              )}
              {activeTab === "xero" && xeroSyncError && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, fontSize: 13, color: "#991B1B" }}>
                  <AlertCircle size={15} style={{ flexShrink: 0 }} /> {xeroSyncError}
                </div>
              )}
            </div>
          )}

          {searchQuery && filteredInvoices.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, fontSize: 13, color: "#166534" }}>
              <span>Found <strong>{filteredInvoices.length}</strong> invoice{filteredInvoices.length !== 1 ? "s" : ""} matching "<strong>{searchQuery}</strong>"</span>
              <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#166534" }}>Clear search</button>
            </div>
          )}

          {isLoading && activeTab === "xero" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 0", gap: 10, color: "#64748B", fontSize: 14 }}>
              <span className="spinner" /> Loading Xero invoices…
            </div>
          )}
          {isLoadingQB && activeTab === "quickbooks" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 0", gap: 10, color: "#64748B", fontSize: 14 }}>
              <span className="spinner" /> Loading QuickBooks invoices…
            </div>
          )}
          {isLoadingSage && activeTab === "sage" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 0", gap: 10, color: "#64748B", fontSize: 14 }}>
              <span className="spinner" /> Loading Sage invoices…
            </div>
          )}
          {isLoadingBC && activeTab === "businesscentral" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 0", gap: 10, color: "#64748B", fontSize: 14 }}>
              <span className="spinner" /> Loading Business Central invoices…
            </div>
          )}
          {isLoadingKeap && activeTab === "keap" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 0", gap: 10, color: "#64748B", fontSize: 14 }}>
              <span className="spinner" /> Loading Keap invoices…
            </div>
          )}

          {!isLoading && searchQuery && !filteredInvoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon"><Search size={24} style={{ color: "#94A3B8" }} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No matching invoices</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 360 }}>No invoices found for "<strong>{searchQuery}</strong>".</div>
              <button className="xero-dl-all-btn" style={{ marginTop: 8 }} onClick={() => setSearchQuery("")}>Clear search</button>
            </div>
          )}

          {isClientRangeTab && appliedRange && !searchQuery && baseList.length > 0 && !activeList.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon"><FileText size={24} style={{ color: "#94A3B8" }} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No invoices in this range</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>No synced {rangeTab?.label} invoices are dated {formatDate(appliedRange.from)} – {formatDate(appliedRange.to)}. Sync this range to pull any that haven't been fetched yet.</div>
              <button className="xero-dl-all-btn" style={{ marginTop: 8 }} onClick={handleClearRange}>Clear date range</button>
            </div>
          )}

          {/* Xero — not connected (same layout as the other accounting tabs) */}
          {activeTab === "xero" && !isLoading && !searchQuery && xeroNotConnected && !invoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#E0F5FD" }}><XeroLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Connect Xero</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>Link your Xero account to sync overdue invoices, preview PDFs, send chase emails, and raise Pre-LBAs.</div>
              <button className="xero-connect-btn" style={{ marginTop: 8 }} onClick={handleXeroConnect}>
                <Link size={15} /> Connect Xero
              </button>
            </div>
          )}

          {/* Xero — connected, nothing to show (no range applied) */}
          {activeTab === "xero" && !isLoading && !searchQuery && xeroConnected && accessMessage && !invoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#E0F5FD" }}><XeroLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No Xero invoices yet</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>{accessMessage}</div>
              <button className="xero-sync-btn" style={{ marginTop: 8 }} onClick={() => navigate("/setting-account2")}>
                <Settings size={15} /> Go to Xero settings
              </button>
            </div>
          )}

          {/* Xero — connected, nothing in the applied range */}
          {activeTab === "xero" && !isLoading && !searchQuery && xeroConnected && !invoices.length && !accessMessage && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#E0F5FD" }}><XeroLogo size={28} /></div>
              {appliedRange ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No invoices in this range</div>
                  <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>No synced Xero invoices are dated {formatDate(appliedRange.from)} – {formatDate(appliedRange.to)}. Sync this range from Xero to pull any that haven't been fetched yet.</div>
                  <button className="xero-sync-btn" style={{ marginTop: 8 }} onClick={handleXeroRangeSync} disabled={isSyncingXero}>
                    {isSyncingXero ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync this range</>}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No Xero invoices yet</div>
                  <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>No Xero invoices with PDF attachments were found.</div>
                  <button className="xero-sync-btn" style={{ marginTop: 8 }} onClick={() => navigate("/setting-account2")}>
                    <Settings size={15} /> Go to Xero settings
                  </button>
                </>
              )}
            </div>
          )}

          {activeTab === "uploaded" && !searchQuery && !uploadedInvoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#F5F3FF" }}><CloudUpload size={24} style={{ color: "#7C3AED" }} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No uploaded invoices</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>Upload your own PDF invoices to preview, send, and chase payment — just like Xero invoices.</div>
              <button className="xero-upload-btn" style={{ marginTop: 8 }} onClick={() => setUploadModalOpen(true)}>
                <Upload size={15} /> Upload your first invoice
              </button>
            </div>
          )}

          {activeTab === "quickbooks" && !isLoadingQB && !searchQuery && !qbConnected && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#DCFCE7" }}><QBLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Connect QuickBooks</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>Link your QuickBooks account to sync overdue invoices, preview PDFs, send chase emails, and raise Pre-LBAs.</div>
              <button className="qb-connect-btn" style={{ marginTop: 8 }} onClick={handleQBConnect} disabled={connectingProvider === "quickbooks"}>
                {connectingProvider === "quickbooks" ? <><span className="spinner-green" /> Connecting…</> : <><Link size={15} /> Connect QuickBooks</>}
              </button>
            </div>
          )}

          {activeTab === "quickbooks" && !isLoadingQB && !searchQuery && qbConnected && !qbInvoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#DCFCE7" }}><QBLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No QuickBooks invoices yet</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>Click "Sync QB" to pull your overdue invoices from QuickBooks.</div>
              <button className="qb-sync-btn" style={{ marginTop: 8 }} onClick={handleQBSync} disabled={isSyncingQB}>
                {isSyncingQB ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync now</>}
              </button>
            </div>
          )}

          {activeTab === "sage" && !isLoadingSage && !searchQuery && !sageConnected && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#DCFCE7" }}><SageLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Connect Sage</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>Link your Sage Business Cloud account to sync overdue invoices, preview PDFs, send chase emails, and raise Pre-LBAs.</div>
              <button className="sage-connect-btn" style={{ marginTop: 8 }} onClick={handleSageConnect} disabled={connectingProvider === "sage"}>
                {connectingProvider === "sage" ? <><span className="spinner-green" /> Connecting…</> : <><Link size={15} /> Connect Sage</>}
              </button>
            </div>
          )}

          {activeTab === "sage" && !isLoadingSage && !searchQuery && sageConnected && !sageInvoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#DCFCE7" }}><SageLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No Sage invoices yet</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>Click "Sync Sage" to pull your overdue invoices from Sage.</div>
              <button className="sage-sync-btn" style={{ marginTop: 8 }} onClick={handleSageSync} disabled={isSyncingSage}>
                {isSyncingSage ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync now</>}
              </button>
            </div>
          )}

          {activeTab === "businesscentral" && !isLoadingBC && !searchQuery && !bcConnected && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#DBEAFE" }}><BCLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Connect Business Central</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>Link your Microsoft Dynamics 365 Business Central account to sync overdue invoices, preview PDFs, send chase emails, and raise Pre-LBAs.</div>
              <button className="bc-connect-btn" style={{ marginTop: 8 }} onClick={handleBCConnect} disabled={connectingProvider === "businesscentral"}>
                {connectingProvider === "businesscentral" ? <><span className="spinner-green" /> Connecting…</> : <><Link size={15} /> Connect Business Central</>}
              </button>
            </div>
          )}

          {activeTab === "businesscentral" && !isLoadingBC && !searchQuery && bcConnected && !bcInvoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#DBEAFE" }}><BCLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No Business Central invoices yet</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>Click "Sync BC" to pull your outstanding invoices from Business Central.</div>
              <button className="bc-sync-btn" style={{ marginTop: 8 }} onClick={handleBCSync} disabled={isSyncingBC}>
                {isSyncingBC ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync now</>}
              </button>
            </div>
          )}

          {activeTab === "keap" && !isLoadingKeap && !searchQuery && !keapConnected && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#FFEDD5" }}><KeapLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Connect Keap</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 400 }}>Link your Keap account to sync overdue invoices, preview PDFs, send chase emails, and raise Pre-LBAs.</div>
              <button className="keap-connect-btn" style={{ marginTop: 8 }} onClick={handleKeapConnect} disabled={connectingProvider === "keap"}>
                {connectingProvider === "keap" ? <><span className="spinner-green" /> Connecting…</> : <><Link size={15} /> Connect Keap</>}
              </button>
            </div>
          )}

          {activeTab === "keap" && !isLoadingKeap && !searchQuery && keapConnected && !keapInvoices.length && (
            <div className="xero-empty">
              <div className="xero-empty-icon" style={{ background: "#FFEDD5" }}><KeapLogo size={28} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>No Keap invoices yet</div>
              <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380 }}>Click "Sync Keap" to pull your outstanding invoices from Keap.</div>
              <button className="keap-sync-btn" style={{ marginTop: 8 }} onClick={handleKeapSync} disabled={isSyncingKeap}>
                {isSyncingKeap ? <><span className="spinner-green" /> Syncing…</> : <><RefreshCw size={15} /> Sync now</>}
              </button>
            </div>
          )}

          {/* Invoice grid */}
          {!isLoading && !isLoadingQB && !isLoadingSage && !isLoadingBC && !isLoadingKeap && filteredInvoices.length > 0 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
                {currentInvoices.map((invoice) => {
                  const isQB        = invoice._isQuickBooks;
                  const isSage      = invoice._isSage;
                  const isBC        = invoice._isBusinessCentral;
                  const isKeap      = invoice._isKeap;
                  const invoiceId   = getInvoiceId(invoice);
                  const statusKey   = isQB ? "QUICKBOOKS" : isSage ? invoice.status : isBC ? invoice.status : isKeap ? invoice.status : invoice._isUploaded ? "UPLOADED" : invoice.status;
                  const statusStyle = getStatusStyle(statusKey);
                  const overdue     = isOverdue(invoice.dueDate) && invoice.status !== "PAID";
                  return (
                    <div key={invoiceId} className={`dd-card${invoice._isUploaded ? " uploaded-card" : isQB ? " qb-card" : isSage ? " sage-card" : isBC ? " bc-card" : isKeap ? " keap-card" : ""}`}>
                      <div className={`xero-card-header${invoice._isUploaded ? " uploaded-header" : isQB ? " qb-header" : isSage ? " sage-header" : isBC ? " bc-header" : isKeap ? " keap-header" : ""}`}>
                        <div>
                          <div className="xero-invoice-number" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                            {invoice.xeroInvoiceNumber || invoice.fileName || "No number"}
                            {invoice._isUploaded && <span className="xero-uploaded-tag"><Upload size={9} /> Uploaded</span>}
                            {isQB && <span className="xero-qb-tag"><QBLogo size={9} /> QuickBooks</span>}
                            {isSage && <span className="xero-sage-tag"><SageLogo size={9} /> Sage</span>}
                            {isBC && <span className="xero-bc-tag"><BCLogo size={9} /> Business Central</span>}
                            {isKeap && <span className="xero-keap-tag"><KeapLogo size={9} /> Keap</span>}
                            {overdue && <span className="xero-overdue-tag"><AlertCircle size={9} /> OVERDUE</span>}
                          </div>
                          <div className="xero-contact-name">{invoice.xeroContactName || (invoice._isUploaded ? "Manually uploaded" : isQB ? "QuickBooks contact" : isSage ? "Sage contact" : isBC ? "Business Central contact" : isKeap ? "Keap contact" : "Unknown contact")}</div>
                        </div>
                        <span className="xero-status-badge" style={statusStyle}>
                          ● {isQB ? (invoice.status || "QB") : isSage ? (invoice.status || "Sage") : isBC ? (invoice.status || "BC") : isKeap ? (invoice.status || "Keap") : invoice._isUploaded ? "UPLOADED" : invoice.status}
                        </span>
                      </div>

                      <div className="dd-card-body">
                        <div className="xero-detail-row">
                          <span className="xero-detail-label">Amount due</span>
                          <span className="xero-detail-value mono">
                            {formatCurrency(
                              invoice.amountDue || parseFloat(String(invoice.totalAmounts || "0").replace(/,/g, "")) || 0,
                              invoice.currency
                            )}
                          </span>
                        </div>
                        <div className="xero-detail-row">
                          <span className="xero-detail-label">Due date</span>
                          <span className="xero-detail-value" style={overdue ? { color: "#DC2626" } : {}}>{formatDate(invoice.dueDate)}</span>
                        </div>
                        {!invoice._isUploaded && invoice.xeroPdfFileSize && (
                          <div className="xero-detail-row">
                            <span className="xero-detail-label">File size</span>
                            <span className="xero-detail-value">{(invoice.xeroPdfFileSize / 1024).toFixed(2)} KB</span>
                          </div>
                        )}
                        {invoice._isUploaded && invoice.fileSize && (
                          <div className="xero-detail-row">
                            <span className="xero-detail-label">File size</span>
                            <span className="xero-detail-value">{(invoice.fileSize / 1024).toFixed(2)} KB</span>
                          </div>
                        )}
                        {(invoice.xeroContactEmail || invoice.contactEmail) && (
                          <div className="xero-detail-row">
                            <span className="xero-detail-label">Contact</span>
                            <span className="xero-detail-value" style={{ fontSize: 12, color: "#64748B", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{invoice.xeroContactEmail || invoice.contactEmail}</span>
                          </div>
                        )}
                        {invoice.xeroDescription && (
                          <div className="xero-description-row" title={invoice.xeroDescription}>
                            {invoice.xeroDescription.length > 80 ? invoice.xeroDescription.slice(0, 80) + "…" : invoice.xeroDescription}
                          </div>
                        )}
                      </div>

                      <div className="dd-card-footer">
                        <CTAs type="Secondarybtn" text="Preview" width="auto" onClick={() => handlePreview(invoice)} />
                        <CTAs type="Primarybtn" text={isCheckingStripe ? "Loading…" : "Send to Pre-LBA"} width="auto" onClick={() => handleSendToLBA(invoice)} disabled={isCheckingStripe} />
                        <button className="xero-email-btn" onClick={() => setSendEmailInvoice(invoice)}>
                          <Mail size={13} /> Email
                        </button>
                        <button className="xero-icon-btn" onClick={() => handleDownload(invoice)} title="Download PDF">
                          <Download size={15} style={{ color: "#059669" }} />
                        </button>
                        {invoice._isUploaded && (
                          <button className="xero-icon-btn danger" onClick={() => handleDeleteUploadedInvoice(invoice)} title="Delete">
                            <X size={15} style={{ color: "#DC2626" }} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 20, borderTop: "1px solid #E2E8F0" }}>
                  <button className="xero-page-btn" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><ChevronLeft size={16} /> Previous</button>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      const page = totalPages <= 7 ? i + 1 : currentPage <= 4 ? i + 1 : currentPage >= totalPages - 3 ? totalPages - 6 + i : currentPage - 3 + i;
                      return <button key={page} onClick={() => handlePageChange(page)} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid", borderColor: page === currentPage ? "#2563EB" : "#E2E8F0", background: page === currentPage ? "#EFF6FF" : "#ffffff", color: page === currentPage ? "#2563EB" : "#1E293B", fontFamily: "DM Sans, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>{page}</button>;
                    })}
                  </div>
                  <button className="xero-page-btn" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next <ChevronRight size={16} /></button>
                </div>
              )}
            </>
          )}

          <Footer />
        </div>
      </div>

      {uploadModalOpen && (
        <UploadInvoiceModal
          API_URL={API_URL}
          onClose={() => setUploadModalOpen(false)}
          onUploaded={(newInvoices) => {
            setUploadedInvoices(prev => [...newInvoices.map(inv => ({ ...inv, _isUploaded: true })), ...prev]);
            setActiveTab("uploaded");
          }}
        />
      )}

      {emailSettingsOpen && (
        <EmailSettingsModal
          API_URL={API_URL}
          onClose={() => setEmailSettingsOpen(false)}
          onSaved={({ logoDataUrl, paymentDetails }) => {
            setDefaultLogo(logoDataUrl);
            setDefaultPaymentDetails(paymentDetails);
          }}
        />
      )}

      {sendEmailInvoice && (
        <SendEmailModal
          invoice={sendEmailInvoice}
          onClose={() => setSendEmailInvoice(null)}
          onSend={handleSendEmail}
          defaultLogo={defaultLogo}
          defaultPaymentDetails={defaultPaymentDetails}
        />
      )}

      {selectedInvoice && (
        <PortalPopup overlayColor="rgba(0,0,0,0.5)" placement="Centered" onOutsideClick={closePreview}>
          <div className="xero-modal">
            <div className="xero-modal-header">
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", fontFamily: "DM Mono, monospace", display: "flex", alignItems: "center", gap: 8 }}>
                  {selectedInvoice.xeroInvoiceNumber || selectedInvoice.fileName}
                  {selectedInvoice._isUploaded && <span style={{ fontSize: 11, fontWeight: 700, color: "#7C3AED", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 6, padding: "2px 7px", fontFamily: "DM Sans, sans-serif" }}>Uploaded</span>}
                  {selectedInvoice._isQuickBooks && <span style={{ fontSize: 11, fontWeight: 700, color: "#15803D", background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 6, padding: "2px 7px", fontFamily: "DM Sans, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}><QBLogo size={11} /> QuickBooks</span>}
                  {selectedInvoice._isSage && <span style={{ fontSize: 11, fontWeight: 700, color: "#15803D", background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 6, padding: "2px 7px", fontFamily: "DM Sans, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}><SageLogo size={11} /> Sage</span>}
                  {selectedInvoice._isBusinessCentral && <span style={{ fontSize: 11, fontWeight: 700, color: "#1E40AF", background: "#DBEAFE", border: "1px solid #93C5FD", borderRadius: 6, padding: "2px 7px", fontFamily: "DM Sans, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}><BCLogo size={11} /> Business Central</span>}
                  {selectedInvoice._isKeap && <span style={{ fontSize: 11, fontWeight: 700, color: "#C2410C", background: "#FFEDD5", border: "1px solid #FED7AA", borderRadius: 6, padding: "2px 7px", fontFamily: "DM Sans, sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}><KeapLogo size={11} /> Keap</span>}
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{selectedInvoice.xeroContactName || (selectedInvoice._isQuickBooks ? "QuickBooks invoice" : selectedInvoice._isSage ? "Sage invoice" : selectedInvoice._isBusinessCentral ? "Business Central invoice" : selectedInvoice._isKeap ? "Keap invoice" : "Manually uploaded invoice")}</div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button className="xero-dl-all-btn" style={{ margin: 0 }} onClick={() => handleDownload(selectedInvoice)}><Download size={14} /> Download</button>
                <button className="xero-icon-btn" onClick={closePreview}><X size={15} style={{ color: "#64748B" }} /></button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: "hidden", padding: 16 }}>
              {isLoadingPreview ? (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, color: "#64748B", fontSize: 14 }}><span className="spinner" /> Loading preview…</div>
              ) : previewUrl ? (
                <iframe src={previewUrl} style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }} title="Invoice preview" />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                  <FileText size={48} style={{ color: "#CBD5E1" }} />
                  <p style={{ color: "#64748B", fontSize: 14 }}>Failed to load preview.</p>
                  <button className="xero-dl-all-btn" style={{ margin: 0 }} onClick={() => handleDownload(selectedInvoice)}><Download size={14} /> Download instead</button>
                </div>
              )}
            </div>
          </div>
        </PortalPopup>
      )}

      {isUpdatePaymentOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeUpdatePayment}>
          <UpdatePayment
            onClose={closeUpdatePayment}
            onConnect={handleStripeConnect}
            onStripeDisconnect={handleStripeDisconnect}
            stripeAccountStatus={stripeAccountStatus}
            paystackAccountStatus={paystackAccountStatus}
            onPaystackConnected={handlePaystackConnected}
            onPaystackDisconnect={handlePaystackDisconnect}
            onContinue={() => {
              // Carry the invoice the user clicked "Send to Pre-LBA" on into the form.
              if (pendingInvoice) proceedToLBAForm(pendingInvoice);
              else navigate("/lbaform");
            }}
          />
        </PortalPopup>
      )}
      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeNavigationDropdown}>
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default XeroInvoices;